import React, { useMemo, useState } from "react";
import { lgamma } from "../lib/power";
import ClustersChart from "./charts/ClustersChart";
import ClusterProfilesChart from "./charts/ClusterProfilesChart";
import DendrogramChart from "./charts/DendrogramChart";
import SilhouetteByKChart from "./charts/SilhouetteByKChart";
import SilhouettePlotChart from "./charts/SilhouettePlotChart";
import GroupEllipsesChart from "./charts/GroupEllipsesChart";
import DiscriminantAxesChart from "./charts/DiscriminantAxesChart";
import R2BlocksChart from "./charts/R2BlocksChart";
import AddedVariableChart from "./charts/AddedVariableChart";
import SimpleSlopesChart from "./charts/SimpleSlopesChart";
import JohnsonNeymanChart from "./charts/JohnsonNeymanChart";
import PartialScatterChart from "./charts/PartialScatterChart";
import BinomialChart from "./charts/BinomialChart";
import PriorPosteriorChart from "./charts/PriorPosteriorChart";
import BfRobustnessChart from "./charts/BfRobustnessChart";
import CoincidenceChart from "./charts/CoincidenceChart";
import DotPlotChart from "./charts/DotPlotChart";
import ModeWord from "./charts/ModeWord";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER, ON_FILL, CELL, NODE_EDGE, OBSERVED_FILL, LATENT_FILL, TINT_1, DANGER, RAMP_HIGH, RAMP_NEG, ramp as rampTo } from "./charts/palette";

// Illustrations for key terms, drawn from the running example's data.
// Green (chart-2) is always the example's data; teal (chart-1) is the idea being illustrated.
// Colors come from charts/palette.js as CSS variables (var(--chart-1) …), set for light and dark mode
// in app/assets/stylesheets/charts.css; never write a hex literal here or in charts/*.jsx.
// Props:
//   kind: sd | se | df | groups | ranks | scatter | paired | profiles | f | chi2 | bars
//     | histogram | qq | identity | bland_altman | loadings | heatmap | rank_scatter | samples_scatter
//     | interaction | path | residuals | group_lines | pair_counting | icc | information | scree | dif_bars | agreement | roc | matrix
//     | path_diagram | within_between | caterpillar | msv_dots | item_scatter
//     | clusters | cluster_profiles | dendrogram | silhouette_by_k | silhouette_plot
//     | group_ellipses | discriminant_axes | r2_blocks | added_variable
//     | simple_slopes | johnson_neyman | partial_scatter | binomial | binomial_power | prior_posterior | bf_robustness | coincidence
//     | dot_plot
//   (the same list, in the same order, as TestKeyTerm::ILLUSTRATIONS). The kinds from clusters on are
//   drawn in charts/*.jsx; each file's header comment gives its props.
//   values (sd, se, histogram), groups { name: [values] } (groups, ranks), pairs [[a, b]] (paired, identity),
//   series [[score per condition]] + labels (profiles), points [[x, y]] (scatter),
//   df (df, chi2), df1 + df2 + dfNote (f), observed + symbol (f, chi2), categories + series [{ name, values }] + unit + legendTitle (bars)
//   axisLabel (the outcome's axis), noun (one data point, e.g. "student"), xLabel, yLabel, labels [a, b] (paired)
//   The newer kinds take the props ChartData (app/lib/chart_data.rb) builds, plus settings:
//   histogram { normal, box }, qq { log }, pair_counting { row, column } (the cell to count from).
//   loadings: items [{ name, loading, group? }], with groups [{ name, ave?, cr? }] + correlations, or omega + alpha.
//   heatmap: items + matrix, with blocks [{ name, start, size }] (constructs) or diagonal (reliabilities).
//   icc, information: items [{ key, name, a, b }], focus, peak { theta, info, se }, thetaRange.
//   scree: eigenvalues, random95, randomMean, retained. dif_bars: bars' props, each series with counts [[k, n]].
//   agreement: mode "table" (the crosstab's props, ordered) or "patterns" (patterns, raters, meanAgreement).
//   roc: points [{ fpr, tpr, cutoff, tp, fp, tn, fn }], auc, ci?, cases, controls, marked [{ …point, j }], scoreLabel.
//   matrix: rowLabels, colLabels, matrix [[0–1]], counts?, rowTitle, colTitle, format (proportion | percent).
//   profiles by group: groups [{ name, total, ids, series, means, present, possible }] + labels + legendTitle.
//   path_diagram: graph { panels, nodes, edges }. within_between: clusters, withinSlope, betweenSlope, betweenIntercept.
//   caterpillar: effects [{ id, estimate, lower, upper, n }], sd. msv_dots: constructs [{ name, ave, msv, asv, partner, r }].
//   item_scatter: items [{ name, p, r, d?, rescored? }], difficultyBand, cutoffs, discriminationLabels.

const W = 640, H = 220;
const M = { top: 16, right: 16, bottom: 36, left: 16 };
const PW = W - M.left - M.right, PH = H - M.top - M.bottom;

const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
const sd = (xs) => { const m = mean(xs); return Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1)); };
const normalPdf = (x, mu, s) => Math.exp(-0.5 * ((x - mu) / s) ** 2) / (s * Math.sqrt(2 * Math.PI));
const tPdf = (t, v) => Math.exp(lgamma((v + 1) / 2) - lgamma(v / 2) - 0.5 * Math.log(v * Math.PI) - ((v + 1) / 2) * Math.log(1 + (t * t) / v));
const chi2Pdf = (x, k) => x <= 0 ? 0 : Math.exp((k / 2 - 1) * Math.log(x) - x / 2 - (k / 2) * Math.log(2) - lgamma(k / 2));
const fPdf = (x, a, b) => x <= 0 ? 0 : Math.exp(lgamma((a + b) / 2) - lgamma(a / 2) - lgamma(b / 2) + (a / 2) * Math.log(a / b) + (a / 2 - 1) * Math.log(x) - ((a + b) / 2) * Math.log(1 + (a * x) / b));
const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const dfLabel = (df) => (Number.isInteger(df) ? String(df) : df.toFixed(2));

// Nice axis bounds and ticks around a set of values.
function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 6, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	// Don't run a non-negative scale below zero.
	const min = Math.max(lo >= 0 ? 0 : -Infinity, Math.floor((lo - span * pad) / step) * step), max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

// kind: dot (default), line, dash (a threshold line), square (a shaded area), ring (a marked estimate).
function Legend({ items, title }) {
	const swatch = (kind, color) => {
		switch (kind) {
			case "line": return <span className="inline-block h-0.5 w-4 rounded" style={{ background: color }} />;
			case "dash": return <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} />;
			case "square": return <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />;
			case "ring": return <span className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px]" style={{ borderColor: INK }} />;
			default: return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
		}
	};
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{title && <span className="font-semibold text-gray-700">{title}</span>}
			{items.map(({ label, color, kind }, i) => (
				<span key={typeof label === "string" ? label : i} className="flex items-center gap-1.5">
					{swatch(kind, color)}
					{label}
				</span>
			))}
		</div>
	);
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

function XAxis({ ax, x, label, base }) {
	return (
		<>
			<line x1={M.left} x2={W - M.right} y1={base} y2={base} stroke={GRID} />
			{ax.ticks.map((t) => <text key={t} x={x(t)} y={base + 18} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>)}
			{label && <text x={W - M.right} y={H - 2} fontSize="11" fill={MUTED} textAnchor="end">{label}</text>}
		</>
	);
}

// Stack dots that share a value so every score stays visible.
function stack(values, x, base, r) {
	const seen = {};
	return values.map((v) => {
		const key = v.toFixed(2);
		seen[key] = (seen[key] || 0) + 1;
		return { v, cx: x(v), cy: base - r - (seen[key] - 1) * (r * 2 + 1) };
	});
}

function SdChart({ values, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const ax = axis(values);
	const x = (v) => M.left + ((v - ax.min) / (ax.max - ax.min)) * PW;
	const base = M.top + PH, r = 5;
	const dots = useMemo(() => stack(values, x, base, r), [values]);
	const m = mean(values), s = sd(values);
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Mean ± 1 SD", color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img"
				aria-label={`${values.length} scores. The mean is ${fmt(m)}; most fall within one standard deviation, from ${fmt(m - s)} to ${fmt(m + s)}.`}>
				<rect x={x(m - s)} y={M.top} width={x(m + s) - x(m - s)} height={PH} fill={CHART_1} opacity="0.1" />
				<line x1={x(m)} x2={x(m)} y1={M.top} y2={base} stroke={CHART_1} strokeWidth="2" />
				<text x={x(m) + 6} y={M.top + 12} fontSize="11" fill={INK} fontWeight="600">Mean {fmt(m)}</text>
				<text x={x(m + s) + 6} y={M.top + 28} fontSize="11" fill={MUTED}>+1 SD ({fmt(m + s)})</text>
				<text x={x(m - s) - 6} y={M.top + 28} fontSize="11" fill={MUTED} textAnchor="end">−1 SD ({fmt(m - s)})</text>
				<XAxis ax={ax} x={x} label={axisLabel} base={base} />
				{dots.map((d, i) => (
					<circle key={i} cx={d.cx} cy={d.cy} r={r} fill={CHART_2} stroke={PAPER} strokeWidth="2"
						onMouseEnter={() => setHover(d)} onMouseLeave={() => setHover(null)} />
				))}
				{hover && <Tooltip x={hover.cx} lines={[`One ${noun}`, fmt(hover.v)]} />}
			</svg>
		</figure>
	);
}

function SeChart({ values, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const ax = axis(values);
	const x = (v) => M.left + ((v - ax.min) / (ax.max - ax.min)) * PW;
	const base = M.top + PH, r = 4;
	const dots = useMemo(() => stack(values, x, base, r), [values]);
	const m = mean(values), s = sd(values), n = values.length, se = s / Math.sqrt(n);
	const peak = normalPdf(m, m, se);
	const y = (density) => base - (density / peak) * (PH - 8);
	const path = useMemo(() => {
		const pts = [];
		const step = (ax.max - ax.min) / 300;
		for (let v = ax.min; v <= ax.max; v += step) pts.push(`${pts.length ? "L" : "M"}${x(v).toFixed(1)},${y(normalPdf(v, m, se)).toFixed(1)}`);
		return pts.join(" ");
	}, [values]);
	const onMove = (event) => {
		const box = event.currentTarget.getBoundingClientRect();
		const v = ax.min + ((((event.clientX - box.left) / box.width) * W - M.left) / PW) * (ax.max - ax.min);
		setHover(v >= ax.min && v <= ax.max ? v : null);
	};
	return (
		<figure>
			<Legend items={[{ label: `Individual ${noun}s (SD ${fmt(s)})`, color: CHART_2 }, { label: `Where averages of ${n} ${noun}s land (SE ${fmt(se)})`, color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
				aria-label={`Individual scores spread widely, but averages of ${n} would almost all fall between ${fmt(m - 2 * se)} and ${fmt(m + 2 * se)}.`}>
				<path d={`${path} L${x(ax.max)},${base} L${x(ax.min)},${base} Z`} fill={CHART_1} opacity="0.1" />
				<path d={path} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" />
				<XAxis ax={ax} x={x} label={axisLabel} base={base} />
				{dots.map((d, i) => <circle key={i} cx={d.cx} cy={d.cy} r={r} fill={CHART_2} stroke={PAPER} strokeWidth="2" />)}
				{hover !== null && (
					<g pointerEvents="none">
						<line x1={x(hover)} x2={x(hover)} y1={M.top} y2={base} stroke={MUTED} strokeWidth="1" />
						<Tooltip x={x(hover)} lines={[fmt(hover),
							Math.abs(hover - m) < 2 * se ? "A typical sample average" : Math.abs(hover - m) < 3 * se ? "An unusual sample average" : "Almost no sample averages"]} />
					</g>
				)}
			</svg>
		</figure>
	);
}

function DfChart({ df }) {
	const [hover, setHover] = useState(null);
	const lo = -4.5, hi = 4.5, small = 3;
	const x = (t) => M.left + ((t - lo) / (hi - lo)) * PW;
	const base = M.top + PH;
	const top = tPdf(0, Math.max(df, small));
	const y = (d) => base - (d / top) * (PH - 10);
	const curve = (v) => {
		const pts = [];
		for (let t = lo; t <= hi + 1e-9; t += 0.03) pts.push(`${pts.length ? "L" : "M"}${x(t).toFixed(1)},${y(tPdf(t, v)).toFixed(1)}`);
		return pts.join(" ");
	};
	const paths = useMemo(() => ({ small: curve(small), study: curve(df) }), [df]);
	const onMove = (event) => {
		const box = event.currentTarget.getBoundingClientRect();
		const t = lo + ((((event.clientX - box.left) / box.width) * W - M.left) / PW) * (hi - lo);
		setHover(t >= lo && t <= hi ? t : null);
	};
	return (
		<figure>
			<Legend items={[{ label: `This study: df = ${dfLabel(df)}`, color: CHART_2, kind: "line" }, { label: `A tiny study: df = ${small}`, color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
				aria-label={`The t distribution with ${dfLabel(df)} degrees of freedom is nearly normal; with ${small} it has much heavier tails, so a result needs a larger t to count as significant.`}>
				<XAxis ax={{ ticks: [-4, -3, -2, -1, 0, 1, 2, 3, 4] }} x={x} label="t" base={base} />
				<path d={paths.small} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" />
				<path d={paths.study} fill="none" stroke={CHART_2} strokeWidth="2" strokeLinejoin="round" />
				<text x={x(3.15)} y={y(tPdf(3.15, small)) - 8} fontSize="11" fill={MUTED} textAnchor="middle">heavier tails</text>
				{hover !== null && (
					<g pointerEvents="none">
						<line x1={x(hover)} x2={x(hover)} y1={M.top} y2={base} stroke={MUTED} strokeWidth="1" />
						<Tooltip x={x(hover)} lines={[`t = ${fmt(hover)}`, `df ${dfLabel(df)}: ${tPdf(hover, df).toFixed(3)}`, `df ${small}: ${tPdf(hover, small).toFixed(3)}`]} />
					</g>
				)}
			</svg>
		</figure>
	);
}

// One strip of dots per group, with each group's mean and the grand mean:
// between-groups variance is how far the means spread; within-groups is each strip's spread.
// With ranks, the same picture shows each group's ranks and mean rank.
function GroupsChart({ groups, axisLabel, noun, meanLabel = "Group mean", grandLabel = "Grand mean" }) {
	const [hover, setHover] = useState(null);
	const names = Object.keys(groups);
	const all = names.flatMap((n) => groups[n]);
	const ax = axis(all);
	const x = (v) => M.left + 90 + ((v - ax.min) / (ax.max - ax.min)) * (PW - 90);
	const rowH = Math.min(52, (PH - 10) / names.length);
	const grand = mean(all);
	const base = M.top + rowH * names.length + 6;
	const height = base + 36;
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: meanLabel, color: CHART_1, kind: "line" }, { label: grandLabel, color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Scores for ${names.length} groups. ${meanLabel}s: ${names.map((n) => `${n} ${fmt(mean(groups[n]))}`).join(", ")}; ${grandLabel.toLowerCase()} ${fmt(grand)}.`}>
				<line x1={x(grand)} x2={x(grand)} y1={M.top - 4} y2={base} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" />
				{names.map((name, i) => {
					const cy = M.top + rowH * i + rowH / 2;
					const m = mean(groups[name]);
					return (
						<g key={name}>
							<text x={M.left} y={cy} fontSize="12" fill={INK} dominantBaseline="middle">{name}</text>
							{groups[name].map((v, j) => (
								<circle key={j} cx={x(v)} cy={cy + ((j % 5) - 2) * 3} r="4" fill={CHART_2} stroke={PAPER} strokeWidth="1.5"
									onMouseEnter={() => setHover({ name, v, cx: x(v) })} onMouseLeave={() => setHover(null)} />
							))}
							<line x1={x(m)} x2={x(m)} y1={cy - rowH / 2 + 4} y2={cy + rowH / 2 - 4} stroke={CHART_1} strokeWidth="3" />
						</g>
					);
				})}
				<line x1={M.left + 90} x2={W - M.right} y1={base} y2={base} stroke={GRID} />
				{ax.ticks.map((t) => <text key={t} x={x(t)} y={base + 18} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>)}
				{axisLabel && <text x={W - M.right} y={height - 2} fontSize="11" fill={MUTED} textAnchor="end">{axisLabel}</text>}
				{hover && <Tooltip x={hover.cx} lines={[hover.name, fmt(hover.v)]} />}
			</svg>
		</figure>
	);
}

// Scatterplot with the least-squares line.
function ScatterChart({ points, xLabel, yLabel, noun, lineLabel = "Best-fitting straight line", summary }) {
	const [hover, setHover] = useState(null);
	const xs = points.map((p) => p[0]), ys = points.map((p) => p[1]);
	const axX = axis(xs), axY = axis(ys);
	const L = 44, height = 300, B = height - 40;
	const x = (v) => L + ((v - axX.min) / (axX.max - axX.min)) * (W - L - M.right);
	const y = (v) => B - ((v - axY.min) / (axY.max - axY.min)) * (B - M.top);
	const mx = mean(xs), my = mean(ys);
	const slope = xs.reduce((a, v, i) => a + (v - mx) * (ys[i] - my), 0) / xs.reduce((a, v) => a + (v - mx) ** 2, 0);
	const line = (v) => my + slope * (v - mx);
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: lineLabel, color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={summary || `${points.length} points; the best-fitting line rises ${fmt(slope)} ${yLabel} per ${xLabel}.`}>
				{axY.ticks.map((t) => (
					<g key={t}>
						<line x1={L} x2={W - M.right} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={L - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}</text>
					</g>
				))}
				{axX.ticks.map((t) => <text key={t} x={x(t)} y={B + 18} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>)}
				<text x={W - M.right} y={height - 2} fontSize="11" fill={MUTED} textAnchor="end">{xLabel}</text>
				<text x={L} y={M.top - 4} fontSize="11" fill={MUTED}>{yLabel}</text>
				<line x1={x(axX.min)} x2={x(axX.max)} y1={y(line(axX.min))} y2={y(line(axX.max))} stroke={CHART_1} strokeWidth="2" />
				{points.map(([a, b], i) => (
					<circle key={i} cx={x(a)} cy={y(b)} r="4.5" fill={CHART_2} stroke={PAPER} strokeWidth="2"
						onMouseEnter={() => setHover({ a, b, cx: x(a) })} onMouseLeave={() => setHover(null)} />
				))}
				{hover && <Tooltip x={hover.cx} lines={[`One ${noun}`, `${xLabel}: ${fmt(hover.a)}`, `${yLabel}: ${fmt(hover.b)}`]} />}
			</svg>
		</figure>
	);
}

// Each person's scores across the conditions joined by a line, with the condition means.
// Two conditions is the paired chart.
function ProfilesChart({ series, labels, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const k = labels.length;
	const all = series.flat().filter((v) => v !== null);
	const ax = axis(all);
	const height = 300, L = 44, B = height - 40;
	const y = (v) => B - ((v - ax.min) / (ax.max - ax.min)) * (B - M.top);
	const x = (i) => k === 2 ? W * (0.3 + 0.4 * i) : L + 40 + (i / (k - 1)) * (W - L - M.right - 80);
	const means = labels.map((_, i) => mean(series.map((p) => p[i]).filter((v) => v !== null)));
	const line = (p) => p.map((v, i) => v === null ? null : `${x(i)},${y(v)}`).filter(Boolean).join(" ");
	const up = series.filter(([a, b]) => b > a).length, down = series.filter(([a, b]) => b < a).length;
	const missing = series.flat().filter((v) => v === null).length;
	const summary = k === 2
		? `${series.length} pairs of scores: ${up} went up, ${down} went down. Means ${fmt(means[0])} and ${fmt(means[1])}.`
		: `${series.length} ${noun}s across ${k} conditions${missing ? `; ${missing} of ${series.length * k} scores are missing, and lines join across the gaps` : ", each measured in all of them"}. Means${missing ? " of the scores present" : ""}: ${labels.map((l, i) => `${l} ${fmt(means[i])}`).join(", ")}.`;
	return (
		<figure>
			<Legend items={[{ label: k === 2 ? `One ${noun}'s change` : `One ${noun}'s scores`, color: CHART_2, kind: "line" }, { label: k === 2 ? "Mean change" : "Condition means", color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img" aria-label={summary}>
				{ax.ticks.map((t) => (
					<g key={t}>
						<line x1={L} x2={W - M.right} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={L - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}</text>
					</g>
				))}
				{series.map((p, i) => (
					<polyline key={i} points={line(p)} fill="none" stroke={CHART_2} strokeWidth={hover === i ? 3 : 1.5} strokeLinejoin="round"
						opacity={hover === null || hover === i ? 0.8 : 0.25} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
				))}
				<polyline points={line(means)} fill="none" stroke={CHART_1} strokeWidth="3.5" strokeLinejoin="round" />
				{labels.map((l, i) => <text key={l} x={x(i)} y={B + 20} fontSize="12" fill={INK} textAnchor="middle">{l}</text>)}
				<text x={L} y={M.top - 4} fontSize="11" fill={MUTED}>{axisLabel}</text>
				{hover !== null && <Tooltip x={x(k - 1)} lines={[`One ${noun}`, series[hover].map((v) => v === null ? "—" : fmt(v)).join(" → ")]} />}
			</svg>
		</figure>
	);
}

// Percentages as grouped bars, one color per series in fixed order, labeled with their values.
function BarsChart({ categories, series, unit, legendTitle }) {
	const [hover, setHover] = useState(null);
	const colors = series.length === 1 ? [CHART_2] : [CHART_1, CHART_2, CHART_3];
	const height = 260, L = 44, B = height - 40;
	const top = Math.min(100, Math.ceil(Math.max(...series.flatMap((s) => s.values)) / 20) * 20 + 10);
	const ticks = Array.from({ length: Math.floor(top / 20) + 1 }, (_, i) => i * 20);
	const y = (v) => B - (v / top) * (B - M.top);
	const slot = (W - L - M.right) / categories.length;
	const barW = Math.min(56, (slot * 0.7 - 2 * (series.length - 1)) / series.length);
	const groupW = barW * series.length + 2 * (series.length - 1);
	const bx = (c, s) => L + slot * c + (slot - groupW) / 2 + s * (barW + 2);
	const bar = (x0, v) => {
		const y0 = y(v), r = Math.min(4, B - y0);
		return `M${x0},${B} V${y0 + r} Q${x0},${y0} ${x0 + r},${y0} H${x0 + barW - r} Q${x0 + barW},${y0} ${x0 + barW},${y0 + r} V${B} Z`;
	};
	return (
		<figure>
			{series.length > 1 && <Legend title={legendTitle} items={series.map((s, i) => ({ label: s.name, color: colors[i] }))} />}
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={series.map((s) => `${s.name}: ${categories.map((c, i) => `${c} ${s.values[i]}%`).join(", ")}`).join(". ")}>
				{ticks.map((t) => (
					<g key={t}>
						<line x1={L} x2={W - M.right} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={L - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}%</text>
					</g>
				))}
				{series.map((s, si) => s.values.map((v, ci) => (
					<g key={`${si}-${ci}`} onMouseEnter={() => setHover({ si, ci })} onMouseLeave={() => setHover(null)}>
						<rect x={bx(ci, si)} y={M.top} width={barW} height={B - M.top} fill="transparent" />
						<path d={bar(bx(ci, si), v)} fill={colors[si]} opacity={hover === null || (hover.si === si && hover.ci === ci) ? 1 : 0.6} />
						<text x={bx(ci, si) + barW / 2} y={y(v) - 5} fontSize="11" fill={INK} textAnchor="middle">{Math.round(v)}%</text>
					</g>
				)))}
				{categories.map((c, i) => <text key={c} x={L + slot * i + slot / 2} y={B + 18} fontSize="12" fill={INK} textAnchor="middle">{c}</text>)}
				<text x={L} y={M.top - 4} fontSize="11" fill={MUTED}>{unit}</text>
				{hover && <Tooltip x={bx(hover.ci, hover.si)} lines={[series.length > 1 ? `${series[hover.si].name}, ${categories[hover.ci]}` : categories[hover.ci],
					series[hover.si].counts ? `${series[hover.si].values[hover.ci]}%: ${series[hover.si].counts[hover.ci][0]} of ${series[hover.si].counts[hover.ci][1]}` : `${series[hover.si].values[hover.ci]}%`]} />}
			</svg>
		</figure>
	);
}

// The distribution the result is judged against (F or χ²), with the observed value and the area beyond it.
function DistChart({ pdf, name, symbol, dfText, observed }) {
	const [hover, setHover] = useState(null);
	const hi = Math.max(5, observed * 1.4);
	const x = (v) => M.left + (v / hi) * PW;
	const base = M.top + PH;
	const pts = useMemo(() => {
		const out = [];
		for (let v = hi / 600; v <= hi; v += hi / 300) out.push([v, pdf(v)]);
		return out;
	}, [pdf, hi]);
	// A χ² with 1 df runs off to infinity at zero, so then scale to the curve away from the axis.
	const away = Math.max(...pts.filter(([v]) => v >= hi / 25).map((p) => p[1]));
	const top = pts[0][1] > 2 * away ? away : Math.max(...pts.map((p) => p[1]));
	const y = (d) => Math.max(M.top, base - (d / top) * (PH - 10));
	const shown = pts.filter(([, d]) => base - (d / top) * (PH - 10) >= M.top);
	const path = shown.map(([v, d], i) => `${i ? "L" : "M"}${x(v).toFixed(1)},${y(d).toFixed(1)}`).join(" ");
	const tail = pts.filter(([v]) => v >= observed);
	const tailPath = tail.length ? `M${x(observed)},${base} ` + tail.map(([v, d]) => `L${x(v).toFixed(1)},${y(d).toFixed(1)}`).join(" ") + ` L${x(hi)},${base} Z` : "";
	const step = hi > 24 ? 5 : hi > 12 ? 2 : 1;
	const ticks = Array.from({ length: Math.floor(hi / step) + 1 }, (_, i) => i * step);
	const onMove = (event) => {
		const box = event.currentTarget.getBoundingClientRect();
		const v = ((((event.clientX - box.left) / box.width) * W - M.left) / PW) * hi;
		setHover(v > 0 && v <= hi ? v : null);
	};
	return (
		<figure>
			<Legend items={[{ label: `${name} distribution, ${dfText}`, color: CHART_1, kind: "line" }, { label: `This study: ${symbol} = ${fmt(observed)}`, color: CHART_2, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
				aria-label={`The ${name} distribution with ${dfText}. The study's ${symbol} of ${fmt(observed)} sits in its upper tail.`}>
				{tailPath && <path d={tailPath} fill={CHART_2} opacity="0.25" />}
				<path d={path} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" />
				<line x1={x(observed)} x2={x(observed)} y1={M.top} y2={base} stroke={CHART_2} strokeWidth="2" />
				<text x={x(observed) + 6} y={M.top + 12} fontSize="11" fill={INK} fontWeight="600">{symbol} = {fmt(observed)}</text>
				<XAxis ax={{ ticks }} x={x} label={symbol} base={base} />
				{hover !== null && (
					<g pointerEvents="none">
						<line x1={x(hover)} x2={x(hover)} y1={M.top} y2={base} stroke={MUTED} strokeWidth="1" />
						<Tooltip x={x(hover)} lines={[`${symbol} = ${fmt(hover)}`, `density ${pdf(hover).toFixed(3)}`]} />
					</g>
				)}
			</svg>
		</figure>
	);
}

// ---- Shared pieces for the x–y charts below ----

// A linear scale from an axis onto pixels a → b.
const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);
const step = (ax) => (ax.ticks.length > 1 ? ax.ticks[1] - ax.ticks[0] : 1);
// APA style for values that can't exceed 1: ".40", "−.25".
const fmtR = (x, places = 2) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const count = (n) => n.toLocaleString("en-US");
const plural = (n, noun) => `${count(n)} ${n === 1 ? noun : `${noun}s`}`;
const fit = (pts) => {
	const mx = mean(pts.map((p) => p[0])), my = mean(pts.map((p) => p[1]));
	const slope = pts.reduce((a, [x, y]) => a + (x - mx) * (y - my), 0) / pts.reduce((a, [x]) => a + (x - mx) ** 2, 0);
	return { slope, at: (x) => my + slope * (x - mx) };
};
const pearson = (pts) => {
	const mx = mean(pts.map((p) => p[0])), my = mean(pts.map((p) => p[1]));
	const sxy = pts.reduce((a, [x, y]) => a + (x - mx) * (y - my), 0);
	return sxy / Math.sqrt(pts.reduce((a, [x]) => a + (x - mx) ** 2, 0) * pts.reduce((a, [, y]) => a + (y - my) ** 2, 0));
};
// Linear interpolation between order statistics (R's type 7, as the pages compute quartiles).
const quantile = (sorted, p) => {
	const h = (sorted.length - 1) * p, lo = Math.floor(h);
	return sorted[lo] + (h - lo) * (sorted[Math.min(lo + 1, sorted.length - 1)] - sorted[lo]);
};
// Spread labels that would overlap: keep each at least `gap` px from the one above.
function spread(items, gap = 14) {
	const sorted = [...items].sort((a, b) => a.y - b.y);
	sorted.forEach((it, i) => { if (i > 0 && it.y - sorted[i - 1].y < gap) it.y = sorted[i - 1].y + gap; });
	return items;
}

// A tick label with a true minus sign.
const minus = (t) => (t < 0 ? `−${-t}` : t);

// Gridlines and tick labels for a box { l, r, t, b }, with the axis titles.
function Axes({ axX, axY, x, y, box, xLabel, yLabel, yTicks = true, xFormat = minus, yFormat = minus, xLabelY }) {
	return (
		<g>
			{axY.ticks.map((t) => (
				<g key={`y${t}`}>
					<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
					{yTicks && <text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{yFormat(t)}</text>}
				</g>
			))}
			{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{xFormat(t)}</text>)}
			{xLabel && <text x={box.r} y={xLabelY || box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">{xLabel}</text>}
			{yLabel && <text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{yLabel}</text>}
		</g>
	);
}

function Dot({ cx, cy, r = 4.5, color = CHART_2, ring = 2, opacity = 1, onEnter, onLeave }) {
	return <circle cx={cx} cy={cy} r={r} fill={color} stroke={PAPER} strokeWidth={ring} opacity={opacity} onMouseEnter={onEnter} onMouseLeave={onLeave} />;
}

// ---- qq: normal Q-Q plot, with the log scale beside it when asked ----

function QqChart({ raw, logged, log, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const panels = [{ title: "Scores", data: raw, back: (v) => v, yLabel: axisLabel }];
	if (log && logged) panels.push({ title: "Log of the scores", data: logged, back: Math.exp, yLabel: `${axisLabel || "Score"}, log scale`, logScale: true });
	const height = 300, gap = 40, L = 48;
	const pw = (W - L - M.right - gap * (panels.length - 1)) / panels.length;
	const zs = raw.points.map((p) => p[0]);
	const axX = axis(zs, 0.02);
	const drawn = panels.map((p, i) => {
		const box = { l: L + i * (pw + gap), r: L + i * (pw + gap) + pw, t: M.top + 34, b: height - 40 };
		const ys = p.data.points.map((q) => q[1]);
		let axY = axis(ys);
		if (p.logScale) {
			// Place ticks at round raw values so the log scale still reads in the outcome's units.
			const lo = Math.min(...ys), hi = Math.max(...ys), pad = (hi - lo) * 0.05;
			const ticks = axis([Math.exp(lo), Math.exp(hi)], 0).ticks.filter((t) => t > 0 && Math.log(t) >= lo - pad && Math.log(t) <= hi + pad).map(Math.log);
			axY = { min: lo - pad, max: hi + pad, ticks };
		}
		const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
		const line = (z) => p.data.mean + p.data.sd * z;
		// How far the top five scores sit from the line, in SDs: a long right tail bends up.
		const top = p.data.points.slice(-5).reduce((a, [z, v]) => a + (v - line(z)), 0) / 5 / p.data.sd;
		// The line runs across the panel but stops where it would leave the y range.
		const z0 = Math.max(axX.min, (axY.min - p.data.mean) / p.data.sd), z1 = Math.min(axX.max, (axY.max - p.data.mean) / p.data.sd);
		return { ...p, box, axY, x, y, line, z0, z1, r: pearson(p.data.points), top };
	});
	const [first, second] = drawn;
	const tail = first.top > 0.25 ? "The highest scores rise above the line: a longer right tail than a normal distribution has."
		: first.top < -0.25 ? "The highest scores fall below the line: a shorter right tail than a normal distribution has." : "The points follow the line closely.";
	const logText = second ? ` On the log scale they ${second.r > first.r ? "sit closer to" : "don't sit closer to"} the line.` : "";
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Where a normal distribution would put them", color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Normal Q-Q plot of ${raw.points.length} scores: each score against where a normal distribution would put it. ${tail}${logText}`}>
				{drawn.map((p, i) => (
					<g key={p.title}>
						<text x={p.box.l} y={M.top + 4} fontSize="12" fill={INK} fontWeight="600">{p.title}</text>
						<Axes axX={axX} axY={p.axY} x={p.x} y={p.y} box={p.box} yLabel={p.yLabel} xLabel="Normal quantile (z)"
							yFormat={p.logScale ? (t) => Math.round(Math.exp(t)) : minus} />
						<line x1={p.x(p.z0)} x2={p.x(p.z1)} y1={p.y(p.line(p.z0))} y2={p.y(p.line(p.z1))} stroke={CHART_1} strokeWidth="2" />
						{p.data.points.map(([z, v], j) => (
							<Dot key={j} cx={p.x(z)} cy={p.y(v)} r={4} ring={1}
								onEnter={() => setHover({ panel: i, cx: p.x(z), lines: [`One ${noun}`, `Score: ${fmt(p.back(v))}`, `If normal: ${fmt(p.back(p.line(z)))}`] })}
								onLeave={() => setHover(null)} />
						))}
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
		</figure>
	);
}

// ---- identity: time 1 against time 2, with the no-change line ----

function IdentityChart({ pairs, labels, noun }) {
	const [hover, setHover] = useState(null);
	const ax = axis(pairs.flat());
	const size = 300, L = 48, box = { l: L, r: L + size, t: M.top + 14, b: M.top + 14 + size };
	const height = box.b + 40;
	const x = scale(ax, box.l, box.r), y = scale(ax, box.b, box.t);
	const up = pairs.filter(([a, b]) => b > a).length, down = pairs.filter(([a, b]) => b < a).length, same = pairs.length - up - down;
	const notes = [
		[`${plural(up, noun)} above the line`, `scored higher in ${labels[1]}`],
		[`${plural(same, noun)} on the line`, "scored the same both times"],
		[`${plural(down, noun)} below the line`, `scored lower in ${labels[1]}`]
	];
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Same score both times (y = x)", color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${labels[0]} against ${labels[1]} for ${plural(pairs.length, noun)}. ${up} scored higher the second time, ${down} lower and ${same} the same. Points near the diagonal line mean the two sessions agree.`}>
				<Axes axX={ax} axY={ax} x={x} y={y} box={box} xLabel={`${labels[0]} score`} yLabel={`${labels[1]} score`} />
				<line x1={x(ax.min)} x2={x(ax.max)} y1={y(ax.min)} y2={y(ax.max)} stroke={CHART_1} strokeWidth="2" />
				{pairs.map(([a, b], i) => (
					<Dot key={i} cx={x(a)} cy={y(b)} r={4.5}
						onEnter={() => setHover({ cx: x(a), lines: [`One ${noun}`, `${labels[0]}: ${a}`, `${labels[1]}: ${b}`] })} onLeave={() => setHover(null)} />
				))}
				{notes.map(([head, tail], i) => (
					<g key={head}>
						<text x={box.r + 40} y={box.t + 40 + i * 56} fontSize="12" fill={INK} fontWeight="600">{head}</text>
						<text x={box.r + 40} y={box.t + 56 + i * 56} fontSize="11" fill={MUTED}>{tail}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
		</figure>
	);
}

// ---- bland_altman: difference against mean, with the limits of agreement ----

function BlandAltmanChart({ points, meanDiff, lower, upper, labels, noun }) {
	const [hover, setHover] = useState(null);
	const height = 300, box = { l: 44, r: W - 128, t: M.top + 16, b: height - 40 };
	const axX = axis(points.map((p) => p[0])), axY = axis([...points.map((p) => p[1]), lower, upper, 0]);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const outside = points.filter(([, d]) => d < lower || d > upper).length;
	const marks = spread([
		{ y: y(upper), head: "Upper limit", value: upper },
		{ y: y(meanDiff), head: "Mean difference", value: meanDiff },
		{ y: y(lower), head: "Lower limit", value: lower }
	], 30);
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Mean difference", color: CHART_1, kind: "line" },
				{ label: "95% limits of agreement (mean ± 1.96 SD)", color: CHART_1, kind: "dash" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Each ${noun}'s change (${labels[1]} minus ${labels[0]}) against their average score. The mean change is ${fmt(meanDiff)}; 95% of changes should fall between ${fmt(lower)} and ${fmt(upper)}, and ${outside} of ${points.length} fall outside.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel={`Mean of ${labels[0]} and ${labels[1]}`} yLabel={`${labels[1]} − ${labels[0]}`} />
				<line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={MUTED} strokeWidth="1" />
				<line x1={box.l} x2={box.r} y1={y(meanDiff)} y2={y(meanDiff)} stroke={CHART_1} strokeWidth="2" />
				{[lower, upper].map((v) => <line key={v} x1={box.l} x2={box.r} y1={y(v)} y2={y(v)} stroke={CHART_1} strokeWidth="1.5" strokeDasharray="6 4" />)}
				{marks.map((m) => (
					<g key={m.head}>
						<text x={box.r + 8} y={m.y - 3} fontSize="11" fill={MUTED}>{m.head}</text>
						<text x={box.r + 8} y={m.y + 11} fontSize="12" fill={INK} fontWeight="600">{fmt(m.value)}</text>
					</g>
				))}
				{points.map(([a, d], i) => (
					<Dot key={i} cx={x(a)} cy={y(d)} r={4.5}
						onEnter={() => setHover({ cx: x(a), lines: [`One ${noun}`, `Average of the two: ${fmt(a, 1)}`, `Change: ${fmt(d, 0)}`] })} onLeave={() => setHover(null)} />
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
		</figure>
	);
}

// ---- loadings: one bar per item, grouped by construct when there are several ----

// groups [{ name, ave?, cr? }] and each item's `group` come with several constructs; correlations
// [{ a, b, r }] are the factor correlations. omega and alpha come with one. Only what's given is printed.
function LoadingsChart({ items, groups, correlations = [], omega, alpha }) {
	const [hover, setHover] = useState(null);
	const grouped = Array.isArray(groups) && groups.length > 0;
	const guide = grouped && groups.some((g) => g.ave != null); // AVE pages: mark λ² = .50
	const labelW = 130, rowH = 26, headH = 24;
	const top = M.top + 6 + (guide ? 16 : 0);
	const rows = [];
	let yAt = top;
	items.forEach((it, i) => {
		if (grouped && (i === 0 || items[i - 1].group !== it.group)) {
			rows.push({ head: groups.find((g) => g.name === it.group) || { name: it.group }, y: yAt });
			yAt += headH;
		}
		rows.push({ item: it, i, y: yAt });
		yAt += rowH;
	});
	const box = { l: M.left + labelW, r: W - 60, t: top, b: yAt };
	const height = box.b + 26;
	const x = (v) => box.l + Math.max(0, Math.min(1, v)) * (box.r - box.l);
	const ticks = [0, 0.2, 0.4, 0.6, 0.8, 1];
	const range = (its) => { const v = its.map((it) => it.loading); return [Math.min(...v), Math.max(...v)]; };
	const [lo, hi] = range(items);
	const bar = (y0, v, h) => {
		const x1 = x(v), r = Math.min(4, x1 - box.l);
		return `M${box.l},${y0} H${x1 - r} Q${x1},${y0} ${x1},${y0 + r} V${y0 + h - r} Q${x1},${y0 + h} ${x1 - r},${y0 + h} H${box.l} Z`;
	};
	const stats = (g) => [g.ave != null && `AVE ${fmtR(g.ave)}`, g.cr != null && `CR ${fmtR(g.cr)}`].filter(Boolean).join(" · ");
	const phiText = correlations.map((c) => `${c.a} and ${c.b}, φ = ${fmtR(c.r)}`).join("; ");
	const hasOmega = omega != null && alpha != null;
	const summary = grouped
		? groups.map((g) => { const [a, b] = range(items.filter((it) => it.group === g.name)); return `${g.name}: loadings ${fmtR(a)} to ${fmtR(b)}${stats(g) ? `, ${stats(g).replace(" · ", ", ")}` : ""}`; }).join(". ")
		: `Loadings run from ${fmtR(lo)} to ${fmtR(hi)}`;
	const legend = [{ label: grouped ? "Standardized loading: how strongly each item reflects its construct" : "Standardized loading: how strongly each item reflects the shared factor", color: CHART_1, kind: "square" }];
	if (guide) legend.push({ label: "λ = .71: the construct explains half the item's variance", color: MUTED, kind: "dash" });
	return (
		<figure>
			<Legend items={legend} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Standardized loadings of ${items.length} items. ${summary}.${phiText ? ` Factor correlation: ${phiText}.` : ""}${hasOmega ? ` Omega ${fmtR(omega)}, alpha ${fmtR(alpha)}.` : ""}`}>
				{ticks.map((t) => (
					<g key={t}>
						<line x1={x(t)} x2={x(t)} y1={box.t} y2={box.b} stroke={GRID} />
						<text x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{t === 0 || t === 1 ? t : fmtR(t, 1)}</text>
					</g>
				))}
				{guide && (
					<g pointerEvents="none">
						<line x1={x(Math.SQRT1_2)} x2={x(Math.SQRT1_2)} y1={box.t - 4} y2={box.b} stroke={MUTED} strokeWidth="1.5" strokeDasharray="5 4" />
						<text x={x(Math.SQRT1_2)} y={box.t - 8} fontSize="11" fill={MUTED} textAnchor="middle">λ² = .50</text>
					</g>
				)}
				{rows.map((row) => {
					if (row.head) return (
						<text key={`head-${row.head.name}`} x={M.left} y={row.y + headH / 2 + 2} fontSize="12" fill={INK} dominantBaseline="middle">
							<tspan fontWeight="600">{row.head.name}</tspan>
							{stats(row.head) && <tspan fill={MUTED} dx="10">{stats(row.head)}</tspan>}
						</text>
					);
					const { item: it, i } = row;
					const y0 = row.y + 5, h = rowH - 10;
					return (
						<g key={`${it.group || ""}-${it.name}`} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
							<rect x={M.left} y={row.y} width={W - M.left - M.right} height={rowH} fill="transparent" />
							<text x={box.l - 8} y={y0 + h / 2} fontSize="12" fill={INK} textAnchor="end" dominantBaseline="middle">{it.name}</text>
							<path d={bar(y0, it.loading, h)} fill={CHART_1} opacity={hover === null || hover === i ? 1 : 0.6} />
							<Halo x={x(it.loading) + 6} y={y0 + h / 2} fill={INK} dominantBaseline="middle">{fmtR(it.loading)}</Halo>
						</g>
					);
				})}
				{hover !== null && <Tooltip x={x(items[hover].loading)} lines={[items[hover].group ? `${items[hover].name} (${items[hover].group})` : items[hover].name,
					`Loading ${fmtR(items[hover].loading)}`,
					`${Math.round(items[hover].loading ** 2 * 100)}% of its variance ${items[hover].group ? `explained by ${items[hover].group.toLowerCase()}` : "is shared"}`]} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				{grouped
					? <>{summary}.{phiText && ` Factor correlation: ${phiText}.`}{guide && " AVE is the average of a construct's squared loadings, so bars past the dashed line pull it above the .50 guideline."}</>
					: <>{summary}.{hasOmega && <> Every item counts equally in the total, but they reflect the factor with different strengths. Omega ({fmtR(omega)}) uses those loadings to estimate reliability; alpha ({fmtR(alpha)}) doesn't model them{hi - lo > 0.1 && omega > alpha ? ", so it's lower here" : ""}.</>}</>}
			</figcaption>
		</figure>
	);
}

// ---- rank_scatter: the scatter of ranks, with a switch to the raw scores ----

function RankScatterChart({ points, rawPoints, rho, r, xLabel, yLabel, noun }) {
	const [raw, setRaw] = useState(false);
	const button = (active) => `px-2.5 py-1 ${active ? "bg-gray-100 font-semibold text-gray-800" : "text-gray-600 hover:bg-gray-50"}`;
	const lower = (s) => s.charAt(0).toLowerCase() + s.slice(1);
	return (
		<div>
			<div className="mb-2 inline-flex overflow-hidden rounded border border-gray-200 text-xs" role="group" aria-label="Show">
				<button type="button" className={button(!raw)} aria-pressed={!raw} onClick={() => setRaw(false)}>Ranks</button>
				<button type="button" className={`${button(raw)} border-l border-gray-200`} aria-pressed={raw} onClick={() => setRaw(true)}>Raw scores</button>
			</div>
			{raw
				? <ScatterChart points={rawPoints} xLabel={xLabel} yLabel={yLabel} noun={noun} lineLabel={`Least-squares line on the raw scores: r = ${fmtR(r)}`}
					summary={`The raw scores of ${plural(rawPoints.length, noun)}, with the least-squares line; Pearson's r is ${fmtR(r)}.`} />
				: <ScatterChart points={points} xLabel={`Rank of ${lower(xLabel)} (1 = lowest)`} yLabel={`Rank of ${lower(yLabel)}`} noun={noun}
					lineLabel={`Least-squares line on the ranks: rₛ = ${fmtR(rho)}`}
					summary={`Each ${noun}'s rank on both variables, with the least-squares line through the ranks. Pearson's r on these ranks is Spearman's rho, ${fmtR(rho)}; on the raw scores it is ${fmtR(r)}.`} />}
		</div>
	);
}

// ---- samples_scatter: one panel per sample, each with its line and r ----

function SamplesScatterChart({ samples, xLabel, yLabel, noun }) {
	const [hover, setHover] = useState(null);
	const all = samples.flatMap((s) => s.points);
	const axX = axis(all.map((p) => p[0])), axY = axis(all.map((p) => p[1]));
	const height = 300, gap = 28, L = 44;
	const pw = (W - L - M.right - gap * (samples.length - 1)) / samples.length;
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Best-fitting line in each sample", color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${xLabel} against ${yLabel}, one panel per sample: ${samples.map((s) => `${s.name}, ${s.points.length} people, r = ${fmtR(s.r)}`).join("; ")}.`}>
				{samples.map((s, i) => {
					const box = { l: L + i * (pw + gap), r: L + i * (pw + gap) + pw, t: M.top + 34, b: height - 40 };
					const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
					const xs = s.points.map((p) => p[0]), line = fit(s.points);
					const x0 = Math.min(...xs), x1 = Math.max(...xs);
					return (
						<g key={s.name}>
							<text x={box.l} y={M.top + 4} fontSize="12" fill={INK} fontWeight="600">{s.name}: r = {fmtR(s.r)}</text>
							<Axes axX={axX} axY={axY} x={x} y={y} box={box} yTicks={i === 0} yLabel={i === 0 ? yLabel : null} xLabel={i === samples.length - 1 ? xLabel : null} />
							{s.points.map(([a, b], j) => (
								<Dot key={j} cx={x(a)} cy={y(b)} r={3.5} ring={1.5}
									onEnter={() => setHover({ cx: x(a), lines: [s.name, `${xLabel}: ${a}`, `${yLabel}: ${b}`] })} onLeave={() => setHover(null)} />
							))}
							<line x1={x(x0)} x2={x(x1)} y1={y(line.at(x0))} y2={y(line.at(x1))} stroke={CHART_1} strokeWidth="2" />
						</g>
					);
				})}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
		</figure>
	);
}

// ---- interaction: cell means across one factor, one line per level of the other ----

function InteractionChart({ levels, lines, factor, lineFactor, axisLabel, seLabel = "±1 standard error", errorBars = "between" }) {
	const [hover, setHover] = useState(null);
	const colors = [CHART_1, CHART_2, CHART_3];
	const height = 300, box = { l: 52, r: W - 140, t: M.top + 16, b: height - 52 };
	const k = levels.length;
	const axY = axis(lines.flatMap((li) => li.means.flatMap((m, i) => [m - li.ses[i], m + li.ses[i]])), 0.15);
	const y = scale(axY, box.b, box.t);
	const x = (i) => (k === 1 ? (box.l + box.r) / 2 : box.l + 70 + (i / (k - 1)) * (box.r - box.l - 140));
	const dodge = (j) => (j - (lines.length - 1) / 2) * 8;
	const ends = spread(lines.map((li, j) => ({ j, y: y(li.means[k - 1]) + 4 })), 15);
	const change = k === 2 ? lines.map((li) => `${li.name}: ${fmt(li.means[1] - li.means[0])}`).join("; ") : "";
	return (
		<figure>
			<Legend title={`${lineFactor}:`} items={[...lines.map((li, j) => ({ label: li.name, color: colors[j] })), { label: seLabel, color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Cell means of ${axisLabel || "the outcome"} across ${factor.toLowerCase()}, one line per ${lineFactor.toLowerCase()}. ${k === 2 ? `Change from ${levels[0]} to ${levels[1]}: ${change}. ` : ""}Lines that aren't parallel show an interaction.`}>
				{axY.ticks.map((t) => (
					<g key={t}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}</text>
					</g>
				))}
				{levels.map((l, i) => <text key={l} x={x(i)} y={box.b + 18} fontSize="12" fill={INK} textAnchor="middle">{l}</text>)}
				<text x={(x(0) + x(k - 1)) / 2} y={box.b + 38} fontSize="11" fill={MUTED} textAnchor="middle">{factor}</text>
				{axisLabel && <text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{axisLabel}</text>}
				{lines.map((li, j) => (
					<g key={li.name}>
						<polyline points={li.means.map((m, i) => `${x(i) + dodge(j)},${y(m)}`).join(" ")} fill="none" stroke={colors[j]} strokeWidth="2" strokeLinejoin="round" />
						{li.means.map((m, i) => (
							<g key={i}>
								<line x1={x(i) + dodge(j)} x2={x(i) + dodge(j)} y1={y(m - li.ses[i])} y2={y(m + li.ses[i])} stroke={colors[j]} strokeWidth="1.5" />
								<Dot cx={x(i) + dodge(j)} cy={y(m)} r={5} color={colors[j]}
									onEnter={() => setHover({ cx: x(i), lines: [`${li.name}, ${levels[i]}`, `Mean ${fmt(m)} (SE ${fmt(li.ses[i])})`, `n = ${li.ns[i]}`] })}
									onLeave={() => setHover(null)} />
							</g>
						))}
						<text x={x(k - 1) + dodge(lines.length - 1) + 12} y={ends[j].y} fontSize="12" fill={INK}>{li.name}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			{errorBars === "mixed" && (
				<figcaption className="mt-1 text-xs text-gray-600">
					Error bars are within-subject standard errors, worked out within each {lineFactor.toLowerCase()}. They leave out how much people differ from each other overall, as the tests of {factor.toLowerCase()} and the interaction do, so they show how precisely each {lineFactor.toLowerCase()}'s change is measured. Comparing the {lineFactor.toLowerCase()}s at a single {factor.toLowerCase()} brings those differences between people back in, so that comparison is less precise than the bars suggest.
				</figcaption>
			)}
			{errorBars === "repeated" && (
				<figcaption className="mt-1 text-xs text-gray-600">
					Error bars are within-subject standard errors. They leave out how much people differ from each other overall, as the within-subjects tests do, so they show how precisely the differences between conditions are measured, not how precisely each mean is pinned down on its own.
				</figcaption>
			)}
		</figure>
	);
}

// ---- path: the mediation diagram ----

function PathChart({ a, b, cPrime, c, ab, ci, names }) {
	const [hover, setHover] = useState(null);
	const id = React.useId().replace(/:/g, "");
	const height = 240, bw = 196, bh = 46;
	const X = { x: M.left + bw / 2, y: 180 }, Me = { x: W / 2, y: 44 }, Y = { x: W - M.right - bw / 2, y: 180 };
	const paths = [
		{ key: "a", from: [X.x + 30, X.y - bh / 2], to: [Me.x - bw / 2 + 10, Me.y + bh / 2 + 2], color: CHART_1, text: `a = ${fmt(a, 3)}`, lx: -12, ly: -10, anchor: "end",
			lines: ["Path a", `${names[0]} → ${names[1]}`, `${fmt(a, 3)} per unit of ${names[0].toLowerCase()}`] },
		{ key: "b", from: [Me.x + bw / 2 - 10, Me.y + bh / 2 + 2], to: [Y.x - 30, Y.y - bh / 2 - 2], color: CHART_1, text: `b = ${fmt(b, 3)}`, lx: 12, ly: -10, anchor: "start",
			lines: ["Path b", `${names[1]} → ${names[2]}`, `${fmt(b, 3)} per unit of ${names[1].toLowerCase()}, holding ${names[0].toLowerCase()} fixed`] },
		{ key: "c", from: [X.x + bw / 2, X.y], to: [Y.x - bw / 2 - 4, Y.y], color: MUTED, text: `c′ = ${fmt(cPrime, 3)}`, lx: 0, ly: 20, anchor: "middle",
			lines: ["Path c′ (direct)", `${names[0]} → ${names[2]}`, `${fmt(cPrime, 3)} per unit, holding ${names[1].toLowerCase()} fixed`] }
	];
	const boxes = [[X, names[0]], [Me, names[1]], [Y, names[2]]];
	return (
		<figure>
			<Legend items={[{ label: "Indirect path: a, then b", color: CHART_1, kind: "line" }, { label: "Direct path: c′", color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Mediation paths. ${names[0]} to ${names[1]}: a = ${fmt(a, 3)}. ${names[1]} to ${names[2]}: b = ${fmt(b, 3)}. Direct path: c′ = ${fmt(cPrime, 3)}. Indirect effect a × b = ${fmt(ab, 3)}; total effect c = ${fmt(c, 3)}.`}>
				<defs>
					{[CHART_1, MUTED].map((color, i) => (
						<marker key={color} id={`${id}-arrow${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
							<path d="M0,0 L10,5 L0,10 Z" fill={color} />
						</marker>
					))}
				</defs>
				{paths.map((p) => {
					const mx = (p.from[0] + p.to[0]) / 2, my = (p.from[1] + p.to[1]) / 2;
					return (
						<g key={p.key} onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}>
							<line x1={p.from[0]} y1={p.from[1]} x2={p.to[0]} y2={p.to[1]} stroke="transparent" strokeWidth="16" />
							<line x1={p.from[0]} y1={p.from[1]} x2={p.to[0]} y2={p.to[1]} stroke={p.color} strokeWidth={hover?.key === p.key ? 3 : 2}
								markerEnd={`url(#${id}-arrow${p.color === MUTED ? 1 : 0})`} />
							<text x={mx + p.lx} y={my + p.ly} fontSize="12" fill={INK} fontWeight="600" textAnchor={p.anchor}>{p.text}</text>
						</g>
					);
				})}
				{boxes.map(([pos, name]) => (
					<g key={name}>
						<rect x={pos.x - bw / 2} y={pos.y - bh / 2} width={bw} height={bh} rx="6" fill={PAPER} stroke={NODE_EDGE} />
						<text x={pos.x} y={pos.y} fontSize="12" fill={INK} textAnchor="middle" dominantBaseline="middle">{name}</text>
					</g>
				))}
				{hover && <Tooltip x={(hover.from[0] + hover.to[0]) / 2 - 60} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Indirect effect: a × b = {fmt(a, 3)} × {fmt(b, 3)} = {fmt(ab, 3)}{ci ? `, 95% bootstrap CI [${fmt(ci[0], 3)}, ${fmt(ci[1], 3)}]` : ""}. Total effect c = {fmt(c, 3)}, the direct and indirect effects together.
			</figcaption>
		</figure>
	);
}

// ---- residuals: residual against predicted value ----

function ResidualsChart({ points, outcomeName, noun }) {
	const [hover, setHover] = useState(null);
	const height = 280, box = { l: 44, r: W - M.right, t: M.top + 16, b: height - 40 };
	const big = Math.max(...points.map((p) => Math.abs(p[1])));
	const axX = axis(points.map((p) => p[0])), axY = axis([-big, big]);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const sorted = [...points].sort((p, q) => p[0] - q[0]), half = Math.floor(sorted.length / 2);
	const spreadOf = (pts) => Math.sqrt(pts.reduce((s, p) => s + p[1] ** 2, 0) / pts.length);
	return (
		<figure>
			<Legend items={[{ label: `One ${noun}`, color: CHART_2 }, { label: "Zero: the prediction was exactly right", color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${points.length} residuals against predicted ${outcomeName}, centered on zero. Their typical size is ${fmt(spreadOf(sorted.slice(0, half)))} for the lower half of predictions and ${fmt(spreadOf(sorted.slice(half)))} for the upper half.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel={`Predicted ${outcomeName}`} yLabel="Residual (observed − predicted)" />
				<line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={CHART_1} strokeWidth="2" />
				{points.map(([p, e], i) => (
					<Dot key={i} cx={x(p)} cy={y(e)} r={4}
						onEnter={() => setHover({ cx: x(p), lines: [`One ${noun}`, `Predicted: ${fmt(p)}`, `Residual: ${fmt(e)}`] })} onLeave={() => setHover(null)} />
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
		</figure>
	);
}

// ---- group_lines: ANCOVA's parallel lines and adjusted means ----

function GroupLinesChart({ groups, slope, grandX, xLabel, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const colors = [CHART_1, CHART_2, CHART_3];
	const height = 320, box = { l: 44, r: W - 150, t: M.top + 16, b: height - 40 };
	const all = groups.flatMap((g) => g.points);
	const axX = axis(all.map((p) => p[0])), axY = axis([...all.map((p) => p[1]), ...groups.map((g) => g.adjusted)]);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const line = (g) => (v) => g.adjusted + slope * (v - grandX);
	const labels = spread(groups.map((g, i) => ({ i, y: y(line(g)(axX.max)) + 4 })), 15);
	return (
		<figure>
			<Legend items={[...groups.map((g, i) => ({ label: g.name, color: colors[i] })), { label: "Adjusted mean, where each line crosses the mean covariate", kind: "ring" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${axisLabel || "Outcome"} against ${xLabel} by group, with parallel lines of slope ${fmt(slope)}. At the covariate's mean of ${fmt(grandX, 1)}, the adjusted means are ${groups.map((g) => `${g.name} ${fmt(g.adjusted)}`).join(", ")}.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel={xLabel} yLabel={axisLabel} />
				<line x1={x(grandX)} x2={x(grandX)} y1={box.t} y2={box.b} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" />
				<text x={x(grandX) + 5} y={box.b - 6} fontSize="11" fill={MUTED}>Mean {fmt(grandX, 1)}</text>
				{groups.map((g, i) => g.points.map(([a, b], j) => (
					<Dot key={`${i}-${j}`} cx={x(a)} cy={y(b)} r={3.5} ring={1} color={colors[i]} opacity={hover === null || hover.i === i ? 0.8 : 0.25}
						onEnter={() => setHover({ i, cx: x(a), lines: [`One ${noun}, ${g.name}`, `${xLabel}: ${a}`, `${axisLabel || "Outcome"}: ${b}`] })} onLeave={() => setHover(null)} />
				)))}
				{groups.map((g, i) => (
					<g key={g.name}>
						<line x1={x(axX.min)} x2={x(axX.max)} y1={y(line(g)(axX.min))} y2={y(line(g)(axX.max))} stroke={colors[i]} strokeWidth="2" />
						<circle cx={x(grandX)} cy={y(g.adjusted)} r="6" fill={colors[i]} stroke={INK} strokeWidth="1.5"
							onMouseEnter={() => setHover({ i, cx: x(grandX), lines: [g.name, `Adjusted mean ${fmt(g.adjusted)}`, `Unadjusted mean ${fmt(g.mean)}`] })} onMouseLeave={() => setHover(null)} />
						<text x={box.r + 8} y={labels[i].y} fontSize="12" fill={INK}>{g.name} <tspan fill={MUTED}>{fmt(g.adjusted)}</tspan></text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
		</figure>
	);
}

// ---- histogram: one outcome's distribution, with its mean and median (or a normal curve) ----

function HistogramChart({ values, normal, box: showBox, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const sorted = [...values].sort((a, b) => a - b), n = values.length;
	const ax = axis(values, 0.02);
	const width = step(ax) / ((ax.max - ax.min) / step(ax) < 10 ? 2 : 1);
	const bins = [];
	for (let lo = ax.min; lo < ax.max - 1e-9; lo += width) bins.push({ lo, hi: lo + width, n: 0 });
	values.forEach((v) => { const b = bins[Math.min(bins.length - 1, Math.max(0, Math.floor((v - ax.min) / width + 1e-9)))]; b.n += 1; });
	const m = mean(values), s = sd(values), med = quantile(sorted, 0.5);
	const L = 44, t = M.top + 18, hb = t + 160;
	const height = hb + (showBox ? 76 : 40);
	const x = scale(ax, L, W - M.right);
	const curve = (v) => normalPdf(v, m, s) * n * width;
	const top = Math.max(...bins.map((b) => b.n), normal ? curve(m) : 0);
	const axY = axis([0, top], 0);
	const y = scale(axY, hb, t);
	const barPath = (b) => {
		const x0 = x(b.lo) + 1, x1 = x(b.hi) - 1, y0 = y(b.n), r = Math.min(4, (x1 - x0) / 2, hb - y0);
		return `M${x0},${hb} V${y0 + r} Q${x0},${y0} ${x0 + r},${y0} H${x1 - r} Q${x1},${y0} ${x1},${y0 + r} V${hb} Z`;
	};
	const curvePath = useMemo(() => {
		const pts = [];
		for (let v = ax.min; v <= ax.max; v += (ax.max - ax.min) / 200) pts.push(`${pts.length ? "L" : "M"}${x(v).toFixed(1)},${y(curve(v)).toFixed(1)}`);
		return pts.join(" ");
	}, [values]);
	const q1 = quantile(sorted, 0.25), q3 = quantile(sorted, 0.75), iqr = q3 - q1;
	const inside = sorted.filter((v) => v >= q1 - 1.5 * iqr && v <= q3 + 1.5 * iqr);
	const outliers = sorted.filter((v) => v < q1 - 1.5 * iqr || v > q3 + 1.5 * iqr);
	const boxY = hb + 42;
	const [right, left] = m >= med ? [["Mean", m, CHART_1], ["Median", med, MUTED]] : [["Median", med, MUTED], ["Mean", m, CHART_1]];
	const skewText = m - med > 0.05 * s ? "The mean sits right of the median, pulled by a long right tail."
		: med - m > 0.05 * s ? "The mean sits left of the median, pulled by a long left tail." : "The mean and median nearly coincide.";
	return (
		<figure>
			<Legend items={normal
				? [{ label: `${noun.charAt(0).toUpperCase() + noun.slice(1)}s in each range`, color: CHART_2, kind: "square" }, { label: `Normal curve with the same mean and SD (${fmt(m)}, ${fmt(s)})`, color: CHART_1, kind: "line" }]
				: [{ label: `${noun.charAt(0).toUpperCase() + noun.slice(1)}s in each range`, color: CHART_2, kind: "square" }, { label: "Mean", color: CHART_1, kind: "line" }, { label: "Median", color: MUTED, kind: "line" },
					...(showBox ? [{ label: "Beyond 1.5 IQR", color: CHART_2 }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Histogram of ${n} scores. Mean ${fmt(m)}, median ${fmt(med)}. ${normal ? "The normal curve with the same mean and SD is drawn over it." : skewText}${showBox ? ` Box plot: quartiles ${fmt(q1)} and ${fmt(q3)}; ${outliers.length} scores lie more than 1.5 IQR beyond them.` : ""}`}>
				{axY.ticks.map((tk) => (
					<g key={tk}>
						<line x1={L} x2={W - M.right} y1={y(tk)} y2={y(tk)} stroke={GRID} />
						<text x={L - 6} y={y(tk)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{tk}</text>
					</g>
				))}
				{bins.map((b, i) => (
					<g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
						<rect x={x(b.lo)} y={t} width={x(b.hi) - x(b.lo)} height={hb - t} fill="transparent" />
						{b.n > 0 && <path d={barPath(b)} fill={CHART_2} opacity={hover === null || hover === i ? 1 : 0.6} />}
					</g>
				))}
				{normal
					? <path d={curvePath} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" pointerEvents="none" />
					: [right, left].map(([name, v, color], i) => (
						<g key={name} pointerEvents="none">
							<line x1={x(v)} x2={x(v)} y1={t - 6} y2={hb} stroke={color} strokeWidth="2" />
							<text x={x(v) + (i === 0 ? 6 : -6)} y={t + 4} fontSize="11" fill={INK} fontWeight="600" textAnchor={i === 0 ? "start" : "end"}>{name} {fmt(v)}</text>
						</g>
					))}
				<line x1={L} x2={W - M.right} y1={hb} y2={hb} stroke={GRID} />
				{ax.ticks.map((tk) => <text key={tk} x={x(tk)} y={hb + 16} fontSize="11" fill={MUTED} textAnchor="middle">{tk}</text>)}
				{axisLabel && <text x={W - M.right} y={height - 2} fontSize="11" fill={MUTED} textAnchor="end">{axisLabel}</text>}
				<text x={L} y={t - 8} fontSize="11" fill={MUTED}>{noun.charAt(0).toUpperCase() + noun.slice(1)}s</text>
				{showBox && (
					<g>
						<line x1={x(inside[0])} x2={x(q1)} y1={boxY} y2={boxY} stroke={MUTED} />
						<line x1={x(q3)} x2={x(inside[inside.length - 1])} y1={boxY} y2={boxY} stroke={MUTED} />
						{[inside[0], inside[inside.length - 1]].map((v, i) => <line key={i} x1={x(v)} x2={x(v)} y1={boxY - 5} y2={boxY + 5} stroke={MUTED} />)}
						<rect x={x(q1)} y={boxY - 8} width={x(q3) - x(q1)} height="16" fill={PAPER} stroke={MUTED} rx="2" />
						<line x1={x(med)} x2={x(med)} y1={boxY - 8} y2={boxY + 8} stroke={INK} strokeWidth="2" />
						{outliers.map((v, i) => <Dot key={i} cx={x(v)} cy={boxY} r={4} ring={1.5}
							onEnter={() => setHover({ outlier: v })} onLeave={() => setHover(null)} />)}
					</g>
				)}
				{hover !== null && (hover.outlier !== undefined
					? <Tooltip x={x(hover.outlier)} lines={[`One ${noun}, beyond 1.5 IQR`, fmt(hover.outlier)]} />
					: <Tooltip x={x(bins[hover].lo)} lines={[`${+bins[hover].lo.toFixed(4)} to ${+bins[hover].hi.toFixed(4)}`, plural(bins[hover].n, noun)]} />)}
			</svg>
		</figure>
	);
}

// ---- heatmap: correlations, one teal ramp (a brown one for negatives) ----

// Near-paper → chart-1 (palette.js), or → chart-3 for negative correlations only.
const ramp = (r) => rampTo(Math.abs(r), r < 0 ? RAMP_NEG : RAMP_HIGH);

// A lower-triangle matrix. blocks [{ name, start, size }] outline each construct's within-construct
// correlations and split the caption's average into within and between. diagonal (with measures)
// puts each measure's reliability on the diagonal, as in a multitrait-multimethod matrix.
function HeatmapChart({ items, matrix, reversed, blocks, diagonal, measures }) {
	const [hover, setHover] = useState(null);
	const id = React.useId().replace(/:/g, "");
	const k = items.length, d = Array.isArray(diagonal) ? 1 : 0;
	const n = k - 1 + d; // rows and columns in the grid
	const noun = measures ? "measure" : "item";
	// Room for the longest row name; column names wider than a column are set at an angle.
	const longest = Math.max(...items.map((s) => s.length));
	const labelW = Math.max(90, Math.min(190, Math.round(longest * 6.6) + 16));
	const cell = Math.min(64, (W - M.left - labelW - M.right) / n);
	const slant = items.slice(0, n).some((s) => s.length * 6.2 > cell - 4);
	const slantH = slant ? Math.round(Math.max(...items.slice(0, n).map((s) => s.length)) * 6.2 * 0.64) : 0;
	const top = M.top + 4, left = M.left + labelW;
	const gridRow = (i) => i - 1 + d;
	const gridB = top + cell * n;
	const height = gridB + 76 + slantH;
	const pairs = [];
	for (let i = 1; i < k; i++) for (let j = 0; j < i; j++) pairs.push({ i, j, v: matrix[i][j] });
	const offDiag = pairs.map((p) => p.v);
	const blockOf = (i) => (blocks || []).findIndex((b) => i >= b.start && i < b.start + b.size);
	const negative = offDiag.some((v) => v < -0.05);
	// The caption's averages: one overall, or within each construct and between them.
	const averages = blocks
		? [...blocks.map((b, bi) => ({ label: `within ${b.name}`, v: mean(pairs.filter((p) => blockOf(p.i) === bi && blockOf(p.j) === bi).map((p) => p.v)) })),
			{ label: blocks.length === 2 ? `between ${blocks[0].name} and ${blocks[1].name}` : "between constructs", v: mean(pairs.filter((p) => blockOf(p.i) !== blockOf(p.j)).map((p) => p.v)) }]
			.filter((a) => !Number.isNaN(a.v))
		: [{ label: `between ${noun}s`, v: mean(offDiag) }];
	// The staircase outline of a construct's within-construct cells.
	const outline = (b) => {
		const s = b.start, e = b.start + b.size - 1, first = s + 1 - d;
		const pts = [[left + s * cell, top + gridRow(first) * cell]];
		for (let i = first; i <= e; i++) pts.push([left + (i + d) * cell, top + gridRow(i) * cell], [left + (i + d) * cell, top + (gridRow(i) + 1) * cell]);
		pts.push([left + s * cell, top + (gridRow(e) + 1) * cell]);
		return `M${pts.map((p) => p.join(",")).join(" L")} Z`;
	};
	const legendX = left, legendW = 180, legendY = height - 22;
	const hovered = hover && (hover.r === hover.c
		? [items[hover.r], `Reliability (α) = ${fmtR(diagonal[hover.r])}`]
		: [`${items[hover.r]} × ${items[hover.c]}`, `r = ${fmtR(matrix[hover.r][hover.c])}`,
			...(blocks ? [blockOf(hover.r) === blockOf(hover.c) ? `Within ${blocks[blockOf(hover.r)]?.name}` : "Between constructs"] : [])]);
	return (
		<figure>
			<Legend items={[{ label: <span>Correlation between two {noun}s: <ModeWord light="darker" dark="brighter" /> is stronger</span>, color: CHART_1, kind: "square" },
				...(negative ? [{ label: "Negative correlation", color: CHART_3, kind: "square" }] : []),
				...(blocks ? [{ label: "Outlined: two items of the same construct", color: INK, kind: "line" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Correlations between ${k} ${noun}s, from ${fmtR(Math.min(...offDiag))} to ${fmtR(Math.max(...offDiag))}. Average ${averages.map((a) => `${a.label} ${fmtR(a.v)}`).join(", ")}.${d ? ` Reliabilities on the diagonal: ${items.map((s, i) => `${s} ${fmtR(diagonal[i])}`).join(", ")}.` : ""}`}>
				<defs>
					<linearGradient id={`${id}-ramp`}>
						{/* Stops every 5%: a gradient between color-mix() stops would otherwise blend in oklab, not the cells' sRGB. */}
						{Array.from({ length: 21 }, (_, s) => s / 20).map((o) => <stop key={o} offset={o} stopColor={ramp(negative ? 2 * o - 1 : o)} />)}
					</linearGradient>
				</defs>
				{items.slice(1 - d).map((_, idx) => {
					const i = idx + 1 - d;
					return <text key={i} x={left - 8} y={top + gridRow(i) * cell + cell / 2} fontSize="12" fill={INK} textAnchor="end" dominantBaseline="middle">{items[i]}</text>;
				})}
				{items.slice(0, n).map((name, c) => {
					const cx = left + c * cell + cell / 2;
					return slant
						? <text key={c} x={cx + 4} y={gridB + 12} fontSize="11" fill={INK} textAnchor="end" transform={`rotate(-40 ${cx + 4} ${gridB + 12})`}>{name}</text>
						: <text key={c} x={cx} y={gridB + 16} fontSize="11" fill={INK} textAnchor="middle">{name}</text>;
				})}
				{d === 1 && items.map((_, i) => (
					<g key={`d${i}`} onMouseEnter={() => setHover({ r: i, c: i })} onMouseLeave={() => setHover(null)}>
						<rect x={left + i * cell + 1} y={top + i * cell + 1} width={cell - 2} height={cell - 2} rx="3" fill={PAPER} stroke={hover && hover.r === i && hover.c === i ? INK : GRID} strokeWidth="1.5" />
						<text x={left + i * cell + cell / 2} y={top + i * cell + cell / 2} fontSize="12" fill={INK} fontStyle="italic" textAnchor="middle" dominantBaseline="middle">({fmtR(diagonal[i])})</text>
					</g>
				))}
				{pairs.map(({ i, j, v }) => (
					<g key={`${i}-${j}`} onMouseEnter={() => setHover({ r: i, c: j })} onMouseLeave={() => setHover(null)}>
						<rect x={left + j * cell + 1} y={top + gridRow(i) * cell + 1} width={cell - 2} height={cell - 2} rx="3" fill={ramp(v)}
							stroke={hover && hover.r === i && hover.c === j ? INK : "none"} strokeWidth="1.5" />
						<text x={left + j * cell + cell / 2} y={top + gridRow(i) * cell + cell / 2} fontSize="12" fill={Math.abs(v) > 0.55 ? ON_FILL : INK} textAnchor="middle" dominantBaseline="middle">{fmtR(v)}</text>
					</g>
				))}
				{(blocks || []).map((b) => {
					const first = b.start + 1 - d;
					return (
						<g key={b.name} pointerEvents="none">
							{b.size > 1 - d && <path d={outline(b)} fill="none" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />}
							<text x={left + (first + d) * cell + 6} y={top + gridRow(first) * cell + cell / 2} fontSize="11" fill={INK} fontWeight="600" dominantBaseline="middle">{b.name}</text>
						</g>
					);
				})}
				<text x={legendX} y={legendY - 8} fontSize="11" fill={MUTED}>Scale</text>
				<rect x={legendX + 40} y={legendY - 18} width={legendW} height="10" rx="2" fill={`url(#${id}-ramp)`} />
				<text x={legendX + 40} y={legendY + 6} fontSize="11" fill={MUTED}>{negative ? "−1" : "0"}</text>
				{negative && <text x={legendX + 40 + legendW / 2} y={legendY + 6} fontSize="11" fill={MUTED} textAnchor="middle">0</text>}
				<text x={legendX + 40 + legendW} y={legendY + 6} fontSize="11" fill={MUTED} textAnchor="end">1</text>
				{hover && <Tooltip x={left + hover.c * cell} lines={hovered} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Average correlation {averages.map((a) => `${a.label}: ${fmtR(a.v)}`).join("; ")}.
				{d ? " The diagonal gives each measure's reliability (Cronbach's α)." : ""}
				{reversed ? " (R) marks a reverse-keyed item, rescored before correlating." : ""}
			</figcaption>
		</figure>
	);
}

// ---- pair_counting: one cell of a crosstab, with the cells that pair concordantly and discordantly with it ----

function PairCountingChart({ rowKeys, colKeys, rowLabels, colLabels, counts, row, column, rowTitle, colTitle, noun }) {
	const [chosen, setChosen] = useState(null);
	const start = [Math.max(0, rowKeys.indexOf(String(row))), Math.max(0, colKeys.indexOf(String(column)))];
	const [ci, cj] = chosen || start;
	const nr = rowKeys.length, nc = colKeys.length;
	const kind = (p, q) => ((p < ci && q < cj) || (p > ci && q > cj) ? "c" : (p < ci && q > cj) || (p > ci && q < cj) ? "d" : null);
	let conc = 0, disc = 0;
	counts.forEach((rowCounts, p) => rowCounts.forEach((v, q) => { if (kind(p, q) === "c") conc += v; if (kind(p, q) === "d") disc += v; }));
	const own = counts[ci][cj];
	const labelW = 118, top = M.top + 36, rowH = 40;
	const left = M.left + labelW, colW = (W - left - M.right) / nc;
	const height = top + rowH * nr + 8;
	return (
		<figure>
			<Legend items={[{ label: `Concordant with the outlined cell: ${plural(conc, noun)}`, color: CHART_1, kind: "square" },
				{ label: `Discordant: ${plural(disc, noun)}`, color: CHART_2, kind: "square" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img" onMouseLeave={() => setChosen(null)}
				aria-label={`The ${plural(own, noun)} in ${rowLabels[ci]}, ${colLabels[cj]} pair concordantly with ${count(conc)} others (${count(own * conc)} pairs) and discordantly with ${count(disc)} (${count(own * disc)} pairs).`}>
				<text x={M.left} y={top - 8} fontSize="11" fill={MUTED}>{rowTitle}</text>
				<text x={left} y={M.top + 2} fontSize="11" fill={MUTED}>{colTitle}</text>
				{colLabels.map((l, q) => <text key={l} x={left + q * colW + colW / 2} y={top - 8} fontSize="11" fill={INK} textAnchor="middle">{l}</text>)}
				{rowLabels.map((l, p) => <text key={l} x={left - 8} y={top + p * rowH + rowH / 2} fontSize="12" fill={INK} textAnchor="end" dominantBaseline="middle">{l}</text>)}
				{counts.map((rowCounts, p) => rowCounts.map((v, q) => {
					const k = kind(p, q), mine = p === ci && q === cj;
					return (
						<g key={`${p}-${q}`} onMouseEnter={() => setChosen([p, q])}>
							<rect x={left + q * colW + 1} y={top + p * rowH + 1} width={colW - 2} height={rowH - 2} rx="3"
								fill={k === "c" ? CHART_1 : k === "d" ? CHART_2 : CELL} fillOpacity={k ? 0.22 : 1}
								stroke={mine ? INK : "none"} strokeWidth="2" />
							<text x={left + q * colW + colW / 2} y={top + p * rowH + rowH / 2} fontSize="13" fill={INK} fontWeight={mine ? 700 : 400}
								textAnchor="middle" dominantBaseline="middle">{v}</text>
						</g>
					);
				}))}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				The {plural(own, noun)} in {rowLabels[ci]}, {colLabels[cj]} pair concordantly with the {count(conc)} above-left and below-right: {count(own)} × {count(conc)} = {count(own * conc)} pairs.
				They pair discordantly with the {count(disc)} above-right and below-left: {count(own)} × {count(disc)} = {count(own * disc)} pairs. Hover over any cell to count from it.
			</figcaption>
		</figure>
	);
}

// ---- icc and information: item response theory's curves ----

const logistic = (it, t) => 1 / (1 + Math.exp(-it.a * (t - it.b)));
const itemInfo = (it, t) => { const p = logistic(it, t); return it.a ** 2 * p * (1 - p); };
const thetaGrid = ([lo, hi], steps = 160) => Array.from({ length: steps + 1 }, (_, i) => lo + ((hi - lo) * i) / steps);
const linePath = (pts, x, y) => pts.map(([a, b], i) => `${i ? "L" : "M"}${x(a).toFixed(1)},${y(b).toFixed(1)}`).join(" ");
const thetaTicks = ([lo, hi]) => Array.from({ length: Math.floor(hi) - Math.ceil(lo) + 1 }, (_, i) => Math.ceil(lo) + i);
// The θ under the pointer, or null outside the plot.
const pointerTheta = (event, box, ax) => {
	const rect = event.currentTarget.getBoundingClientRect();
	const px = ((event.clientX - rect.left) / rect.width) * W;
	return px >= box.l && px <= box.r ? ax.min + ((px - box.l) / (box.r - box.l)) * (ax.max - ax.min) : null;
};
// "a, b and c".
const sentenceList = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);
// Text with a white halo, so a direct label stays legible over lines.
const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;

// Every item's 2PL curve, P(θ) = 1 / (1 + e^(−a(θ − b))), with the focus item's b marked where P = .50.
function IccChart({ items, focus, thetaRange = [-4, 4], xLabel, yLabel }) {
	const [theta, setTheta] = useState(null);
	const [picked, setPicked] = useState(null);
	const height = 300, box = { l: 48, r: W - M.right, t: M.top + 16, b: height - 40 };
	const axX = { min: thetaRange[0], max: thetaRange[1], ticks: thetaTicks(thetaRange) }, axY = { min: 0, max: 1, ticks: [0, 0.25, 0.5, 0.75, 1] };
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const grid = useMemo(() => thetaGrid(thetaRange), [thetaRange[0], thetaRange[1]]);
	const f = items.find((it) => it.key === focus) || items[0];
	const shown = picked !== null ? items[picked] : f;
	const others = items.filter((it) => it !== f);
	const curve = (it) => linePath(grid.map((t) => [t, logistic(it, t)]), x, y);
	// The focus item's name sits beside its curve where P = .85, left of the rise.
	const t85 = Math.min(axX.max - 0.5, f.b + Math.log(0.85 / 0.15) / f.a);
	return (
		<figure>
			<Legend items={[{ label: `${f.name}: a = ${fmt(f.a)}, b = ${fmt(f.b)}`, color: CHART_1, kind: "line" }, { label: `The other ${plural(others.length, "item")}`, color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img" onMouseMove={(e) => setTheta(pointerTheta(e, box, axX))} onMouseLeave={() => { setTheta(null); setPicked(null); }}
				aria-label={`Item characteristic curves for ${items.length} items: each item's chance of a yes rising with the trait. ${f.name} (a = ${fmt(f.a)}, b = ${fmt(f.b)}) crosses a 50% chance at θ = ${fmt(f.b)}, where it is steepest. Difficulties run from ${fmt(Math.min(...items.map((it) => it.b)))} to ${fmt(Math.max(...items.map((it) => it.b)))}.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel={xLabel} yLabel={yLabel} yFormat={(t) => (t === 0 || t === 1 ? t : fmtR(t))} />
				{others.map((it) => {
					const i = items.indexOf(it);
					return (
						<g key={it.key} onMouseEnter={() => setPicked(i)}>
							<path d={curve(it)} fill="none" stroke="transparent" strokeWidth="10" />
							<path d={curve(it)} fill="none" stroke={MUTED} strokeWidth={picked === i ? 2 : 1.5} opacity={picked === i ? 0.9 : 0.35} strokeLinejoin="round" />
						</g>
					);
				})}
				<g pointerEvents="none">
					<line x1={box.l} x2={x(f.b)} y1={y(0.5)} y2={y(0.5)} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" />
					<line x1={x(f.b)} x2={x(f.b)} y1={y(0.5)} y2={box.b} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" />
					<path d={curve(f)} fill="none" stroke={CHART_1} strokeWidth="2.5" strokeLinejoin="round" />
					<circle cx={x(f.b)} cy={y(0.5)} r="4.5" fill={CHART_1} stroke={PAPER} strokeWidth="2" />
					<Halo x={x(f.b) + 6} y={box.b - 6} fill={INK} fontWeight="600">b = {fmt(f.b)}</Halo>
					<Halo x={x(t85) - 8} y={y(0.85)} fill={INK} fontWeight="600" textAnchor="end" dominantBaseline="middle">{f.name}</Halo>
				</g>
				{theta !== null && (
					<g pointerEvents="none">
						<line x1={x(theta)} x2={x(theta)} y1={box.t} y2={box.b} stroke={MUTED} strokeWidth="1" />
						<circle cx={x(theta)} cy={y(logistic(shown, theta))} r="3.5" fill={shown === f ? CHART_1 : MUTED} stroke={PAPER} strokeWidth="1.5" />
						<Tooltip x={x(theta)} lines={[`θ = ${fmt(theta)}`, `${shown.name}: ${Math.round(logistic(shown, theta) * 100)}% chance`,
							...(shown === f ? [] : [`a = ${fmt(shown.a)}, b = ${fmt(shown.b)}`])]} />
					</g>
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each curve is one item. Its difficulty b is the trait level where the chance reaches 50%; its discrimination a sets how steeply it climbs there. Hover over a gray curve to read that item.
			</figcaption>
		</figure>
	);
}

// Test information I(θ) = Σ a²P(1 − P), with each item's share under it, and below it (its own axis,
// not a second one on the same panel) the standard error of a trait estimate, 1/√I(θ).
function InformationChart({ items, peak, thetaRange = [-4, 4], xLabel }) {
	const [theta, setTheta] = useState(null);
	const info = (t) => items.reduce((s, it) => s + itemInfo(it, t), 0);
	const top = { l: 48, r: W - M.right, t: M.top + 20, b: 186 }, low = { l: 48, r: W - M.right, t: 226, b: 306 };
	const height = low.b + 36;
	const axX = { min: thetaRange[0], max: thetaRange[1], ticks: thetaTicks(thetaRange) };
	const grid = useMemo(() => thetaGrid(thetaRange), [thetaRange[0], thetaRange[1]]);
	const axI = axis([0, peak.info * 1.15], 0); // headroom for the peak label
	const ses = grid.map((t) => 1 / Math.sqrt(info(t)));
	// The SE panel stops at four times its lowest value; beyond that the checklist says little anyway.
	const seCap = Math.min(Math.max(...ses), peak.se * 4);
	const axS = axis([0, seCap], 0);
	if (axS.ticks.length > 4) axS.ticks = axS.ticks.filter((_, i) => i % 2 === 0); // a short panel: every other tick
	const x = scale(axX, top.l, top.r), yI = scale(axI, top.b, top.t), yS = scale(axS, low.b, low.t);
	const clip = React.useId().replace(/:/g, "");
	const hover = theta !== null && { t: theta, i: info(theta) };
	return (
		<figure>
			<Legend items={[{ label: "Test information: the items' information added up", color: CHART_1, kind: "line" }, { label: "One item's information", color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img" onMouseMove={(e) => setTheta(pointerTheta(e, top, axX))} onMouseLeave={() => setTheta(null)}
				aria-label={`Test information across the trait for ${items.length} items peaks at ${fmt(peak.info)} at θ = ${fmt(peak.theta)}, where the standard error of a trait estimate is lowest, ${fmt(peak.se)}. Away from the peak information falls and the standard error rises.`}>
				<defs><clipPath id={`${clip}-se`}><rect x={low.l} y={low.t - 2} width={low.r - low.l} height={low.b - low.t + 2} /></clipPath></defs>
				<text x={top.l} y={M.top + 2} fontSize="12" fill={INK} fontWeight="600">Information</text>
				<Axes axX={{ ...axX, ticks: [] }} axY={axI} x={x} y={yI} box={top} />
				{items.map((it) => <path key={it.key} d={linePath(grid.map((t) => [t, itemInfo(it, t)]), x, yI)} fill="none" stroke={MUTED} strokeWidth="1.5" opacity="0.35" />)}
				<path d={linePath(grid.map((t) => [t, info(t)]), x, yI)} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" />
				<line x1={x(peak.theta)} x2={x(peak.theta)} y1={yI(peak.info)} y2={low.b} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" />
				<circle cx={x(peak.theta)} cy={yI(peak.info)} r="4.5" fill={CHART_1} stroke={PAPER} strokeWidth="2" />
				<Halo x={x(peak.theta)} y={yI(peak.info) - 10} fill={INK} fontWeight="600" textAnchor="middle">Peak: {fmt(peak.info)} at θ = {fmt(peak.theta)}</Halo>
				<text x={low.l} y={low.t - 10} fontSize="12" fill={INK} fontWeight="600">Standard error of a trait estimate: 1 / √information</text>
				<Axes axX={axX} axY={axS} x={x} y={yS} box={low} xLabel={xLabel} xLabelY={low.b + 32} />
				<path d={linePath(grid.map((t, i) => [t, ses[i]]), x, yS)} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" clipPath={`url(#${clip}-se)`} />
				<circle cx={x(peak.theta)} cy={yS(peak.se)} r="4.5" fill={CHART_1} stroke={PAPER} strokeWidth="2" />
				<Halo x={x(peak.theta) + 8} y={yS(peak.se) - 8} fill={INK} fontWeight="600">Lowest: {fmt(peak.se)}</Halo>
				{hover && (
					<g pointerEvents="none">
						<line x1={x(hover.t)} x2={x(hover.t)} y1={top.t} y2={low.b} stroke={MUTED} strokeWidth="1" />
						<Tooltip x={x(hover.t)} lines={[`θ = ${fmt(hover.t)}`, `Information ${fmt(hover.i)}`, `Standard error ${fmt(1 / Math.sqrt(hover.i))}`]} />
					</g>
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each item adds the most information near its own difficulty, more the higher its discrimination. Where information is high the standard error is low, so the scale measures most precisely around θ = {fmt(peak.theta)}.
			</figcaption>
		</figure>
	);
}

// ---- scree: eigenvalues by factor, against parallel analysis's random data ----

function ScreeChart({ eigenvalues, random95, randomMean, retained }) {
	const [hover, setHover] = useState(null);
	const k = eigenvalues.length;
	const height = 290, box = { l: 44, r: W - 170, t: M.top + 22, b: height - 40 };
	const axY = axis([0, ...eigenvalues, ...(random95 || []), 1], 0.05);
	const axX = { min: 0.5, max: k + 0.5, ticks: Array.from({ length: k }, (_, i) => i + 1) };
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const pts = (vs) => vs.map((v, i) => [i + 1, v]);
	const half = (x(2) - x(1)) / 2;
	const ends = spread([
		{ key: "obs", y: y(eigenvalues[k - 1]) + 4, text: "Observed", color: INK },
		...(random95 ? [{ key: "r95", y: y(random95[k - 1]) + 4, text: "Random, 95th percentile", color: INK }] : []),
		...(randomMean ? [{ key: "mean", y: y(randomMean[k - 1]) + 4, text: "Random, mean", color: MUTED }] : []),
		{ key: "one", y: y(1) + 4, text: "Eigenvalue of 1", color: MUTED }
	], 14);
	return (
		<figure>
			<Legend items={[{ label: "Observed eigenvalue", color: CHART_2 }, ...(random95 ? [{ label: "Random data, 95th percentile (parallel analysis)", color: CHART_1, kind: "dash" }] : []),
				...(randomMean ? [{ label: "Random data, mean", color: CHART_1, kind: "line" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Scree plot of ${k} eigenvalues, from ${fmt(eigenvalues[0])} down to ${fmt(eigenvalues[k - 1])}.${random95 ? ` The first ${retained} are above the 95th percentile of eigenvalues from random data, so ${plural(retained, "factor")} ${retained === 1 ? "is" : "are"} kept.` : ""}`}>
				{retained > 0 && (
					<g pointerEvents="none">
						<rect x={x(1) - half} y={box.t} width={x(retained) - x(1) + 2 * half} height={box.b - box.t} fill={CHART_2} opacity="0.08" />
						<text x={x(1) - half + 6} y={box.b - 6} fontSize="11" fill={INK} fontWeight="600">Kept: {plural(retained, "factor")}</text>
					</g>
				)}
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel="Factor" yLabel="Eigenvalue" />
				<line x1={box.l} x2={box.r} y1={y(1)} y2={y(1)} stroke={MUTED} strokeWidth="1" />
				{randomMean && <path d={linePath(pts(randomMean), x, y)} fill="none" stroke={CHART_1} strokeWidth="1.5" opacity="0.6" />}
				{random95 && <path d={linePath(pts(random95), x, y)} fill="none" stroke={CHART_1} strokeWidth="2" strokeDasharray="6 4" />}
				<path d={linePath(pts(eigenvalues), x, y)} fill="none" stroke={CHART_2} strokeWidth="2" strokeLinejoin="round" />
				{eigenvalues.map((v, i) => (
					<circle key={i} cx={x(i + 1)} cy={y(v)} r="5" fill={i < retained ? CHART_2 : PAPER} stroke={i < retained ? PAPER : CHART_2} strokeWidth="2" pointerEvents="none" />
				))}
				{ends.map((e) => <text key={e.key} x={box.r + 10} y={e.y} fontSize="11" fill={e.color}>{e.text}</text>)}
				{eigenvalues.map((v, i) => (
					<rect key={`hit${i}`} x={x(i + 1) - half} y={box.t} width={2 * half} height={box.b - box.t} fill="transparent"
						onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
				))}
				{hover !== null && <Tooltip x={x(hover + 1)} lines={[`Factor ${hover + 1}`, `Observed ${fmt(eigenvalues[hover])}`,
					...(random95 ? [`Random 95th percentile ${fmt(random95[hover])}`] : []), ...(randomMean ? [`Random mean ${fmt(randomMean[hover])}`] : []),
					hover < retained ? "Kept" : "Not kept"]} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each eigenvalue is how much of the items' variance one factor accounts for, in items' worth.{random95 ? ` Parallel analysis keeps the factors whose eigenvalue beats the 95th percentile from random data of the same size: here the first ${retained}.` : ""} Filled dots are the factors kept.
			</figcaption>
		</figure>
	);
}

// ---- agreement: two raters' table, or how several raters' codes split ----

function AgreementChart(props) {
	return props.mode === "patterns" ? <AgreementSplits {...props} /> : <AgreementTable {...props} />;
}

// Two raters' table: cells shaded by count, the diagonal (agreement) outlined, and with ordered
// categories the one-step disagreements that weighting gives partial credit dashed.
function AgreementTable({ rowLabels, colLabels, counts, rowTitle, colTitle, ordered, noun }) {
	const [hover, setHover] = useState(null);
	const k = rowLabels.length, total = counts.flat().reduce((a, b) => a + b, 0), most = Math.max(...counts.flat());
	const labelW = Math.max(80, Math.min(160, Math.max(...rowLabels.map((s) => s.length)) * 7 + 16));
	const left = M.left + labelW, top = M.top + 40;
	const colW = Math.min(100, (W - left - M.right) / k), rowH = Math.min(44, colW);
	const height = top + rowH * k + 10;
	const byDistance = Array.from({ length: k }, (_, dist) => counts.reduce((s, row, i) => s + row.reduce((t, v, j) => t + (Math.abs(i - j) === dist ? v : 0), 0), 0));
	const pct = (v) => Math.round((100 * v) / total);
	const steps = ["same rating", "one step apart", "two steps apart", "three steps apart"];
	const distanceText = (dist) => (dist === 0 ? "Same rating: agreement" : ordered ? `${steps[dist] || `${dist} steps apart`}` : "Different ratings");
	return (
		<figure>
			<Legend items={[{ label: <span>{noun.charAt(0).toUpperCase() + noun.slice(1)}s in each cell: <ModeWord light="darker" dark="brighter" /> is more</span>, color: CHART_1, kind: "square" },
				{ label: "Same rating from both (agreement)", color: INK, kind: "line" }, ...(ordered ? [{ label: "One step apart: part credit with weights", color: INK, kind: "dash" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${rowTitle} against ${colTitle} for ${plural(total, noun)}: ${byDistance[0]} (${pct(byDistance[0])}%) got the same rating from both${ordered ? `, ${byDistance.slice(1).map((v, dist) => `${v} ${steps[dist + 1] || `${dist + 1} steps apart`}`).join(", ")}` : `, ${total - byDistance[0]} different ratings`}.`}>
				<text x={M.left} y={top - 8} fontSize="11" fill={MUTED}>{rowTitle}</text>
				<text x={left} y={M.top + 2} fontSize="11" fill={MUTED}>{colTitle}</text>
				{colLabels.map((l, j) => <text key={l} x={left + j * colW + colW / 2} y={top - 8} fontSize="11" fill={INK} textAnchor="middle">{l}</text>)}
				{rowLabels.map((l, i) => <text key={l} x={left - 8} y={top + i * rowH + rowH / 2} fontSize="12" fill={INK} textAnchor="end" dominantBaseline="middle">{l}</text>)}
				{counts.map((row, i) => row.map((v, j) => {
					const t = most ? v / most : 0, dist = Math.abs(i - j), on = hover && hover.i === i && hover.j === j;
					return (
						<g key={`${i}-${j}`} onMouseEnter={() => setHover({ i, j })} onMouseLeave={() => setHover(null)}>
							<rect x={left + j * colW + 2} y={top + i * rowH + 2} width={colW - 4} height={rowH - 4} rx="3" fill={ramp(t)}
								stroke={dist === 0 || (ordered && dist === 1) || on ? INK : "none"} strokeWidth={dist === 0 || on ? 2 : 1.25} strokeDasharray={dist === 1 && ordered && !on ? "4 3" : undefined} />
							<text x={left + j * colW + colW / 2} y={top + i * rowH + rowH / 2} fontSize="13" fill={t > 0.55 ? ON_FILL : INK} fontWeight={dist === 0 ? 600 : 400}
								textAnchor="middle" dominantBaseline="middle">{v}</text>
						</g>
					);
				}))}
				{hover && <Tooltip x={left + hover.j * colW} lines={[`${rowLabels[hover.i]}, ${colLabels[hover.j]}`, `${plural(counts[hover.i][hover.j], noun)} (${pct(counts[hover.i][hover.j])}%)`, distanceText(Math.abs(hover.i - hover.j))]} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				{byDistance[0]} of {plural(total, noun)} ({pct(byDistance[0])}%) got the same rating from both.
				{ordered ? ` Of the rest, ${sentenceList(byDistance.slice(1).map((v, dist) => `${v} ${steps[dist + 1] || `${dist + 1} steps apart`}`))}: weighted kappa counts the near misses as partial agreement.` : ""}
			</figcaption>
		</figure>
	);
}

// Several raters: how each subject's codes split (all agree, 4–1, 3–2 …), with the share of rater
// pairs who agree in each split. The mean of that share over subjects is the observed agreement.
function AgreementSplits({ patterns, raters, subjects, meanAgreement, subjectNoun, raterNoun }) {
	const [hover, setHover] = useState(null);
	const pairsAll = (raters * (raters - 1)) / 2;
	const agreeing = (split) => split.reduce((s, n) => s + (n * (n - 1)) / 2, 0);
	const name = (p) => (p.split.length === 1 ? `All ${raters} agree` : `${p.label} split`);
	const labelW = 220, rowH = 28, top = M.top + 6;
	const box = { l: M.left + labelW, r: W - 60, t: top, b: top + rowH * patterns.length };
	const height = box.b + 34;
	const axX = axis([0, ...patterns.map((p) => p.subjects)], 0);
	const x = scale(axX, box.l, box.r);
	const bar = (y0, v, h) => {
		const x1 = x(v), r = Math.min(4, x1 - box.l);
		return `M${box.l},${y0} H${x1 - r} Q${x1},${y0} ${x1},${y0 + r} V${y0 + h - r} Q${x1},${y0 + h} ${x1 - r},${y0 + h} H${box.l} Z`;
	};
	return (
		<figure>
			<Legend items={[{ label: `${subjectNoun.charAt(0).toUpperCase() + subjectNoun.slice(1)}s with each split of the ${raters} ${raterNoun}s' codes`, color: CHART_2, kind: "square" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`How the ${raters} ${raterNoun}s' codes split across ${plural(subjects, subjectNoun)}: ${patterns.map((p) => `${name(p)}, ${p.subjects}`).join("; ")}. Averaged over ${subjectNoun}s, ${fmtR(meanAgreement)} of ${raterNoun} pairs agree.`}>
				<text x={M.left} y={top - 2} fontSize="11" fill={MUTED}>Split</text>
				<text x={M.left + 104} y={top - 2} fontSize="11" fill={MUTED}>{raterNoun.charAt(0).toUpperCase() + raterNoun.slice(1)} pairs agreeing</text>
				{axX.ticks.map((t) => (
					<g key={t}>
						<line x1={x(t)} x2={x(t)} y1={box.t + 4} y2={box.b} stroke={GRID} />
						<text x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>
					</g>
				))}
				<text x={box.r} y={box.b + 30} fontSize="11" fill={MUTED} textAnchor="end">{subjectNoun.charAt(0).toUpperCase() + subjectNoun.slice(1)}s</text>
				{patterns.map((p, i) => {
					const y0 = box.t + 6 + i * rowH, h = rowH - 10;
					return (
						<g key={p.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
							<rect x={M.left} y={y0 - 5} width={W - M.left - M.right} height={rowH} fill="transparent" />
							<text x={M.left} y={y0 + h / 2} fontSize="12" fill={INK} dominantBaseline="middle">{name(p)}</text>
							<text x={M.left + 104} y={y0 + h / 2} fontSize="12" fill={MUTED} dominantBaseline="middle">{agreeing(p.split)} of {pairsAll} ({fmtR(p.agreement)})</text>
							<path d={bar(y0, p.subjects, h)} fill={CHART_2} opacity={hover === null || hover === i ? 1 : 0.6} />
							<text x={x(p.subjects) + 6} y={y0 + h / 2} fontSize="11" fill={INK} dominantBaseline="middle">{p.subjects}</text>
						</g>
					);
				})}
				{hover !== null && <Tooltip x={x(patterns[hover].subjects)} lines={[name(patterns[hover]), plural(patterns[hover].subjects, subjectNoun),
					`${agreeing(patterns[hover].split)} of ${pairsAll} ${raterNoun} pairs agree (${fmtR(patterns[hover].agreement)})`]} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each {subjectNoun} counts the {raterNoun} pairs who gave it the same code: {pairsAll} pairs when all {raters} agree, fewer as the codes split. The average share over all {plural(subjects, subjectNoun)} is the observed agreement, {fmtR(meanAgreement)}.
			</figcaption>
		</figure>
	);
}

// ---- matrix: a full table of values from 0 to 1, such as transition probabilities ----

// rowLabels, colLabels, matrix [[0–1]], counts (optional, for the tooltip), rowTitle, colTitle,
// format "proportion" (.25) or "percent" (25%). Rows read across: with row proportions each adds to 1.
function MatrixChart({ rowLabels, colLabels, matrix, counts, rowTitle, colTitle, format = "proportion" }) {
	const [hover, setHover] = useState(null);
	const show = (v) => (format === "percent" ? `${Math.round(v * 100)}%` : fmtR(v));
	const nr = rowLabels.length, nc = colLabels.length;
	const labelW = Math.max(80, Math.min(170, Math.max(...rowLabels.map((s) => s.length)) * 7 + 16));
	const left = M.left + labelW, top = M.top + 40;
	const colW = Math.min(100, (W - left - M.right) / nc), rowH = Math.min(44, colW);
	const height = top + rowH * nr + 10;
	const cellText = (i, j) => (counts ? `${show(matrix[i][j])}: ${counts[i][j]} of ${counts[i].reduce((a, b) => a + b, 0)}` : show(matrix[i][j]));
	return (
		<figure>
			<Legend items={[{ label: <span>Share of the row (each {rowTitle.toLowerCase()} row adds to {format === "percent" ? "100%" : "1"}): <ModeWord light="darker" dark="brighter" /> is larger</span>, color: CHART_1, kind: "square" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${rowTitle} by ${colTitle}. ${rowLabels.map((r, i) => `${r}: ${colLabels.map((c, j) => `${c} ${show(matrix[i][j])}`).join(", ")}`).join(". ")}.`}>
				<text x={M.left} y={top - 8} fontSize="11" fill={MUTED}>{rowTitle}</text>
				<text x={left} y={M.top + 2} fontSize="11" fill={MUTED}>{colTitle}</text>
				{colLabels.map((l, j) => <text key={l} x={left + j * colW + colW / 2} y={top - 8} fontSize="11" fill={INK} textAnchor="middle">{l}</text>)}
				{rowLabels.map((l, i) => <text key={l} x={left - 8} y={top + i * rowH + rowH / 2} fontSize="12" fill={INK} textAnchor="end" dominantBaseline="middle">{l}</text>)}
				{matrix.map((row, i) => row.map((v, j) => (
					<g key={`${i}-${j}`} onMouseEnter={() => setHover({ i, j })} onMouseLeave={() => setHover(null)}>
						<rect x={left + j * colW + 2} y={top + i * rowH + 2} width={colW - 4} height={rowH - 4} rx="3" fill={ramp(v)}
							stroke={hover && hover.i === i && hover.j === j ? INK : "none"} strokeWidth="1.5" />
						<text x={left + j * colW + colW / 2} y={top + i * rowH + rowH / 2} fontSize="13" fill={v > 0.55 ? ON_FILL : INK} textAnchor="middle" dominantBaseline="middle">{show(v)}</text>
					</g>
				)))}
				{hover && <Tooltip x={left + hover.j * colW} lines={[`${rowTitle}: ${rowLabels[hover.i]}`, `${colTitle}: ${colLabels[hover.j]}`, cellText(hover.i, hover.j)]} />}
			</svg>
		</figure>
	);
}

// ---- roc: the empirical ROC curve ----

// points [{ fpr, tpr, cutoff, tp, fp, tn, fn }] from (0, 0) to (1, 1), auc, cases, controls, marked
// (the settings' cutoffs, each with j), ci (optional), scoreLabel.
function RocChart({ points, auc, ci, cases, controls, marked = [], scoreLabel, noun }) {
	const [hover, setHover] = useState(null);
	const size = 300, box = { l: 48, t: M.top + 16, r: 48 + size, b: M.top + 16 + size };
	const height = box.b + 40;
	const ax = { min: 0, max: 1, ticks: [0, 0.25, 0.5, 0.75, 1] };
	const x = scale(ax, box.l, box.r), y = scale(ax, box.b, box.t);
	const curve = points.map((p, i) => `${i ? "L" : "M"}${x(p.fpr).toFixed(1)},${y(p.tpr).toFixed(1)}`).join(" ");
	const tick = (t) => (t === 0 || t === 1 ? t : fmtR(t));
	const cut = (p) => `${scoreLabel} ≥ ${+p.cutoff.toFixed(4)}`;
	const aucText = `AUC = ${fmtR(auc)}${ci ? `, 95% CI [${fmtR(ci[0])}, ${fmtR(ci[1])}]` : ""}`;
	const side = box.r + 28;
	return (
		<figure>
			<Legend items={[{ label: "ROC curve: every cutoff of the score", color: CHART_2, kind: "line" }, { label: "Chance (AUC = .50)", color: MUTED, kind: "dash" },
				...(marked.length ? [{ label: "Youden's J at a cutoff: sensitivity − (1 − specificity)", color: CHART_1, kind: "line" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`ROC curve of ${scoreLabel} for ${plural(cases, "case")} and ${plural(controls, "non-case")}. ${aucText}: a randomly chosen case scores higher than a randomly chosen non-case ${Math.round(auc * 100)}% of the time.${marked.map((p) => ` At ${cut(p)}: sensitivity ${fmtR(p.tpr)}, specificity ${fmtR(1 - p.fpr)}, J = ${fmtR(p.j)}.`).join("")}`}>
				<Axes axX={ax} axY={ax} x={x} y={y} box={box} xLabel="1 − specificity (false positive rate)" yLabel="Sensitivity (true positive rate)" xFormat={tick} yFormat={tick} />
				<path d={`${curve} L${x(1)},${y(0)} Z`} fill={CHART_2} opacity="0.12" />
				<line x1={x(0)} y1={y(0)} x2={x(1)} y2={y(1)} stroke={MUTED} strokeWidth="1.5" strokeDasharray="6 4" />
				<path d={curve} fill="none" stroke={CHART_2} strokeWidth="2" strokeLinejoin="round" />
				<Halo x={x(0.55)} y={y(0.18)} fill={INK} fontWeight="600" fontSize="12">{`AUC = ${fmtR(auc)}`}</Halo>
				{marked.map((p) => {
					// A label whose neighbour to the right sits close by goes on the left of its point, so the two don't collide.
					const crowded = marked.some((q) => q !== p && q.fpr > p.fpr && x(q.fpr) - x(p.fpr) < 48 && Math.abs(y(q.tpr) - y(p.tpr)) < 16);
					return (
						<g key={p.cutoff} pointerEvents="none">
							<line x1={x(p.fpr)} x2={x(p.fpr)} y1={y(p.fpr)} y2={y(p.tpr)} stroke={CHART_1} strokeWidth="2" />
							<circle cx={x(p.fpr)} cy={y(p.tpr)} r="5" fill={CHART_2} stroke={INK} strokeWidth="1.5" />
							<Halo x={x(p.fpr) + (crowded ? -9 : 9)} y={y(p.tpr) + 4} textAnchor={crowded ? "end" : "start"} fill={INK} fontWeight="600">≥ {+p.cutoff.toFixed(4)}</Halo>
						</g>
					);
				})}
				{points.map((p, i) => (
					<circle key={i} cx={x(p.fpr)} cy={y(p.tpr)} r="7" fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
				))}
				<text x={side} y={box.t + 8} fontSize="12" fill={INK} fontWeight="600">{aucText}</text>
				<text x={side} y={box.t + 24} fontSize="11" fill={MUTED}>{plural(cases, "case")}, {plural(controls, "non-case")}</text>
				{marked.map((p, i) => (
					<g key={p.cutoff}>
						<text x={side} y={box.t + 60 + i * 58} fontSize="12" fill={INK} fontWeight="600">{cut(p)}</text>
						<text x={side} y={box.t + 76 + i * 58} fontSize="11" fill={MUTED}>Sensitivity {fmtR(p.tpr)} ({p.tp} of {cases})</text>
						<text x={side} y={box.t + 90 + i * 58} fontSize="11" fill={MUTED}>Specificity {fmtR(1 - p.fpr)} ({p.tn} of {controls}), J = {fmtR(p.j)}</text>
					</g>
				))}
				{hover !== null && <Tooltip x={x(points[hover].fpr)} lines={points[hover].cutoff == null
					? ["No one called a case", `Sensitivity 0, specificity 1`]
					: [`Call a case at ${cut(points[hover])}`, `Sensitivity ${fmtR(points[hover].tpr)}: ${points[hover].tp} of ${cases}`,
						`Specificity ${fmtR(1 - points[hover].fpr)}: ${points[hover].tn} of ${controls}`]} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each step is one cutoff: lowering it catches more cases (up) and misflags more non-cases (right). The shaded area is the AUC, the chance that a randomly chosen case scores higher than a randomly chosen non-case (ties count half).
			</figcaption>
		</figure>
	);
}

// ---- path_diagram: a path or SEM diagram laid out on a grid by the page's settings ----

// graph { panels [name], nodes [{ id, label, col, row, latent, panel, details }], edges [{ from, to,
// value, covariance, fixed, omitted, at, bend, details }] }. Measured variables are rectangles and
// latent ones ellipses; paths are arrows labelled with their standardized estimate, fixed loadings
// are gray with the value the model fixes, covariances are curved and double-headed.
const fmtDetail = ({ label, value }) => `${label} = ${/R²|^r$/.test(label) ? fmtR(value) : fmt(value, Math.abs(value) >= 10 ? 2 : 3)}`;
const fmtFixed = (v) => (Number.isInteger(v) ? String(v) : fmt(v));

function PathDiagramChart({ graph }) {
	const [hover, setHover] = useState(null);
	const id = React.useId().replace(/:/g, "");
	const { nodes, edges } = graph;
	const panels = graph.panels.length ? graph.panels : [null];
	const gap = panels.length > 1 ? 36 : 0;
	const pw = (W - M.left - M.right - gap * (panels.length - 1)) / panels.length;
	const rows = Math.max(1, ...nodes.map((n) => n.row));
	const rowH = Math.min(80, 280 / rows);
	const titled = panels[0] !== null;
	const textW = (str) => str.length * 6.6;
	// Each node's label, on two lines when one would be wider than its column allows.
	const shaped = nodes.map((n) => {
		const maxCol = Math.max(0, ...nodes.filter((m) => m.panel === n.panel).map((m) => m.col));
		const room = Math.min(190, maxCol === 0 ? pw : (pw - 16 - 16 * maxCol) / (maxCol + 1)) - 22 - (n.latent ? 12 : 0);
		let lines = [n.label];
		if (textW(n.label) > room && n.label.includes(" ")) {
			const half = n.label.length / 2, at = [...n.label.matchAll(/ /g)].map((m) => m.index).reduce((b, i) => (Math.abs(i - half) < Math.abs(b - half) ? i : b));
			lines = [n.label.slice(0, at), n.label.slice(at + 1)];
		}
		return { ...n, lines, hw: (Math.max(76, Math.max(...lines.map(textW)) + 22) + (n.latent ? 12 : 0)) / 2 };
	});
	const nodeH = Math.max(Math.min(40, rowH * 0.72), shaped.some((n) => n.lines.length > 1) ? 38 : 0);
	// Where a line from a node's center toward (tx, ty) leaves its outline.
	const exit = (n, tx, ty, pad = 2) => {
		const dx = tx - n.cx, dy = ty - n.cy, len = Math.hypot(dx, dy) || 1, ux = dx / len, uy = dy / len;
		const t = n.latent ? 1 / Math.sqrt((ux / n.hw) ** 2 + (uy / n.hh) ** 2) : Math.min(Math.abs(n.hw / (ux || 1e-9)), Math.abs(n.hh / (uy || 1e-9)));
		return [n.cx + ux * (t + pad), n.cy + uy * (t + pad)];
	};
	const layout = (top) => {
		const placed = {};
		panels.forEach((_, p) => {
			const own = shaped.filter((n) => n.panel === p);
			const maxCol = Math.max(0, ...own.map((n) => n.col)), margin = Math.max(...own.map((n) => n.hw)) + 8;
			const left = M.left + p * (pw + gap);
			own.forEach((n) => {
				placed[n.id] = { ...n, cx: maxCol === 0 ? left + pw / 2 : left + margin + (n.col / maxCol) * (pw - 2 * margin), cy: top + n.row * rowH, hh: nodeH / 2 };
			});
		});
		const drawn = edges.map((e, i) => {
			const a = placed[e.from], b = placed[e.to];
			if (e.covariance) {
				const dx = b.cx - a.cx, dy = b.cy - a.cy, len = Math.hypot(dx, dy), bend = e.bend ?? 0.3;
				const c = [(a.cx + b.cx) / 2 - (dy / len) * bend * len, (a.cy + b.cy) / 2 + (dx / len) * bend * len];
				const p0 = exit(a, ...c), p1 = exit(b, ...c);
				const mid = [0.25 * p0[0] + 0.5 * c[0] + 0.25 * p1[0], 0.25 * p0[1] + 0.5 * c[1] + 0.25 * p1[1]];
				return { e, i, d: `M${p0[0]},${p0[1]} Q${c[0]},${c[1]} ${p1[0]},${p1[1]}`, label: mid };
			}
			const p0 = exit(a, b.cx, b.cy), p1 = exit(b, a.cx, a.cy, 3), at = e.at ?? 0.5;
			return { e, i, d: `M${p0[0]},${p0[1]} L${p1[0]},${p1[1]}`, label: [p0[0] + (p1[0] - p0[0]) * at, p0[1] + (p1[1] - p0[1]) * at] };
		});
		return { placed, drawn };
	};
	// Start below the panel titles, lower still when a curved arrow bows up above the top row.
	const base = M.top + (titled ? 30 : 10) + nodeH / 2;
	const arcs = layout(base).drawn.filter((d) => d.e.covariance).map((d) => d.label[1]);
	const top = base + Math.max(0, M.top + (titled ? 30 : 10) + 8 - Math.min(Infinity, ...arcs));
	const { placed, drawn } = layout(top);
	const height = Math.max(top + rows * rowH + nodeH / 2, ...drawn.filter((d) => d.e.covariance).map((d) => d.label[1] + 8)) + 14;
	const color = (e) => (e.fixed || e.omitted ? MUTED : CHART_1);
	const text = (e) => (e.omitted ? "left out" : e.fixed ? fmtFixed(e.value) : fmtR(e.value));
	const arrow = (e) => `${e.from} ${e.covariance ? "↔" : "→"} ${e.to}`;
	const name = (nid) => placed[nid].label;
	const edgeLines = (e) => [`${name(e.from)} ${e.covariance ? "↔" : "→"} ${name(e.to)}`,
		e.omitted ? "Left out of the model: fixed at 0" : e.fixed ? `Loading fixed at ${fmtFixed(e.value)} by the model` : `${e.covariance ? "Correlation" : "Standardized estimate"} ${fmtR(e.value)}`,
		...e.details.map(fmtDetail)];
	const nodeLines = (n) => [n.label, n.latent ? "Latent: estimated from its indicators, not measured" : "Measured variable", ...n.details.map(fmtDetail)];
	const any = (f) => edges.some(f);
	const legend = [{ label: "Measured variable", color: CHART_2, kind: "square" }, ...(nodes.some((n) => n.latent) ? [{ label: "Latent variable", kind: "ring" }] : []),
		...(any((e) => !e.fixed && !e.omitted && !e.covariance) ? [{ label: "Path, with its standardized estimate", color: CHART_1, kind: "line" }] : []),
		...(any((e) => e.covariance) ? [{ label: "Curved, two-headed: correlation", color: CHART_1, kind: "line" }] : []),
		...(any((e) => e.fixed) ? [{ label: "Loading fixed by the model", color: MUTED, kind: "line" }] : []),
		...(any((e) => e.omitted) ? [{ label: "Path left out of the model", color: MUTED, kind: "dash" }] : [])];
	const estimated = edges.filter((e) => !e.fixed && !e.omitted);
	return (
		<figure>
			<Legend items={legend} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Path diagram. ${estimated.map((e) => `${name(e.from)} ${e.covariance ? "with" : "to"} ${name(e.to)}: ${fmtR(e.value)}`).join("; ")}.${any((e) => e.fixed) ? ` Fixed loadings: ${edges.filter((e) => e.fixed).map((e) => `${name(e.from)} to ${name(e.to)} ${fmtFixed(e.value)}`).join(", ")}.` : ""}`}>
				<defs>
					{[CHART_1, MUTED].map((c, k) => (
						<marker key={c} id={`${id}-arrow${k}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
							<path d="M0,0 L10,5 L0,10 Z" fill={c} />
						</marker>
					))}
				</defs>
				{titled && panels.map((p, k) => (
					<g key={p}>
						<text x={M.left + k * (pw + gap)} y={M.top + 4} fontSize="12" fill={INK} fontWeight="600">{p}</text>
						{k > 0 && <line x1={M.left + k * (pw + gap) - gap / 2} x2={M.left + k * (pw + gap) - gap / 2} y1={M.top - 8} y2={height - 6} stroke={GRID} />}
					</g>
				))}
				{drawn.map(({ e, i, d }) => (
					<g key={arrow(e)} onMouseEnter={() => setHover({ edge: i })} onMouseLeave={() => setHover(null)}>
						<path d={d} fill="none" stroke="transparent" strokeWidth="12" />
						<path d={d} fill="none" stroke={color(e)} strokeWidth={hover?.edge === i ? 2.5 : 1.5} strokeDasharray={e.omitted ? "3 4" : undefined}
							markerEnd={`url(#${id}-arrow${color(e) === MUTED ? 1 : 0})`} markerStart={e.covariance ? `url(#${id}-arrow0)` : undefined} />
					</g>
				))}
				{Object.values(placed).map((n) => (
					<g key={n.id} onMouseEnter={() => setHover({ node: n.id })} onMouseLeave={() => setHover(null)}>
						{n.latent
							? <ellipse cx={n.cx} cy={n.cy} rx={n.hw} ry={n.hh} fill={LATENT_FILL} stroke={CHART_1} strokeWidth={hover?.node === n.id ? 2 : 1.5} />
							: <rect x={n.cx - n.hw} y={n.cy - n.hh} width={2 * n.hw} height={2 * n.hh} rx="3" fill={OBSERVED_FILL} stroke={CHART_2} strokeWidth={hover?.node === n.id ? 2 : 1.5} />}
						<text x={n.cx} y={n.cy - (n.lines.length - 1) * 7} fontSize="12" fill={INK} textAnchor="middle" dominantBaseline="middle">
							{n.lines.map((l, k) => <tspan key={k} x={n.cx} dy={k ? 14 : 0}>{l}</tspan>)}
						</text>
					</g>
				))}
				{drawn.map(({ e, i, label }) => (
					<Halo key={`l${arrow(e)}`} x={label[0]} y={label[1]} fontSize={e.fixed || e.omitted ? 11 : 12} fill={e.fixed || e.omitted ? MUTED : INK} fontWeight={e.fixed || e.omitted ? 400 : 600}
						fontStyle={e.omitted ? "italic" : undefined} textAnchor="middle" dominantBaseline="middle"
						onMouseEnter={() => setHover({ edge: i })} onMouseLeave={() => setHover(null)}>{text(e)}</Halo>
				))}
				{hover && (hover.node
					? <Tooltip x={placed[hover.node].cx - 60} lines={nodeLines(placed[hover.node])} />
					: <Tooltip x={drawn[hover.edge].label[0] - 60} lines={edgeLines(edges[hover.edge])} />)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				{titled ? `The ${panels.length} panels are separate models fitted together: ${panels.map((p) => p.toLowerCase()).join(" and ")}. ` : ""}
				{any((e) => !e.fixed && !e.omitted && !e.covariance) ? "Each number on a straight arrow is a standardized estimate: how many SDs the variable at the arrowhead moves per SD of the one at the tail, holding the model's other arrows fixed. " : ""}
				{any((e) => e.covariance) ? "The number on a curved, two-headed arrow is a correlation. " : ""}
				{any((e) => e.fixed) ? "Gray numbers are loadings the model fixes rather than estimates: they define what the latent variables mean. " : ""}
				{any((e) => e.omitted) ? "The dotted arrow is a path the model leaves out, which says it is zero." : ""}
			</figcaption>
		</figure>
	);
}

// ---- within_between: within-cluster and between-cluster slopes ----

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const lowerFirst = (s) => (/^.[A-Z0-9]/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));
// A display name inside a sentence: "Prior Grade" → "prior grade"; acronyms (PHQ-9) stay.
const lowerWords = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");
const article = (w) => `${/^[aeiou]/i.test(w) ? "an" : "a"} ${w}`;
const signed = (v, places = 2) => (v > 0 ? `+${fmt(v, places)}` : fmt(v, places));

// clusters [{ id, x, y, n, sdX, line }], withinSlope (pooled), betweenSlope + betweenIntercept (on the
// cluster means). Sampled clusters (line: true) get a gray within line over ±1 of their own SD of x.
function WithinBetweenChart({ clusters, withinSlope, betweenSlope, betweenIntercept, clusterNoun, xLabel, yLabel, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const height = 320, box = { l: 48, r: W - 176, t: M.top + 18, b: height - 40 };
	const lined = clusters.filter((c) => c.line);
	const seg = (c) => [c.x - c.sdX, c.x + c.sdX].map((v) => [v, c.y + withinSlope * (v - c.x)]);
	const xs = clusters.map((c) => c.x), lo = Math.min(...xs), hi = Math.max(...xs);
	const between = (v) => betweenIntercept + betweenSlope * v;
	const axX = axis([...xs, ...lined.flatMap((c) => seg(c).map((p) => p[0]))]);
	const axY = axis([...clusters.map((c) => c.y), ...lined.flatMap((c) => seg(c).map((p) => p[1])), between(lo), between(hi)], 0.08);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const unit = hi - lo >= 10 ? 10 : 1;
	const cn = clusterNoun, xl = lowerWords(xLabel), yl = lowerWords(yLabel);
	const last = lined.reduce((m, c) => (m && seg(m)[1][0] > seg(c)[1][0] ? m : c), null);
	const ends = spread([{ key: "between", y: y(between(hi)) + 4 }, ...(last ? [{ key: "within", y: y(seg(last)[1][1]) + 4 }] : [])], 30);
	const endOf = (key) => ends.find((e) => e.key === key).y;
	return (
		<figure>
			<Legend items={[{ label: `One ${cn}'s means`, color: CHART_2 }, { label: `Between ${cn}s: least squares on the means`, color: CHART_1, kind: "line" },
				{ label: `Within ${article(cn)}: the pooled within slope, for ${lined.length} of ${clusters.length} ${cn}s`, color: MUTED, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${cap(cn)} means of ${yl} against ${xl} for ${clusters.length} ${cn}s. Within ${article(cn)}, ${yl} changes ${fmt(withinSlope * unit)} per ${unit} of ${xl}; between ${cn}s' means it changes ${fmt(betweenSlope * unit)}.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel={xLabel} yLabel={axisLabel || yLabel} />
				{lined.map((c) => {
					const [[x0, y0], [x1, y1]] = seg(c);
					return (
						<g key={`w${c.id}`} onMouseEnter={() => setHover({ cx: x(c.x), lines: [`${cap(cn)} ${c.id}: within`, `Slope ${fmt(withinSlope, 3)} per 1 of ${xl}`, `Drawn over ±1 SD of its own ${xl}`] })} onMouseLeave={() => setHover(null)}>
							<line x1={x(x0)} x2={x(x1)} y1={y(y0)} y2={y(y1)} stroke="transparent" strokeWidth="8" />
							<line x1={x(x0)} x2={x(x1)} y1={y(y0)} y2={y(y1)} stroke={MUTED} strokeWidth="1.25" opacity="0.7" />
						</g>
					);
				})}
				<g onMouseEnter={() => setHover({ cx: x(hi), lines: [`Between ${cn}s`, `Slope ${fmt(betweenSlope, 3)} per 1 of mean ${xl}`, `Fitted to the ${clusters.length} ${cn} means`] })} onMouseLeave={() => setHover(null)}>
					<line x1={x(lo)} x2={x(hi)} y1={y(between(lo))} y2={y(between(hi))} stroke="transparent" strokeWidth="10" />
					<line x1={x(lo)} x2={x(hi)} y1={y(between(lo))} y2={y(between(hi))} stroke={CHART_1} strokeWidth="2" />
				</g>
				{clusters.map((c) => (
					<Dot key={c.id} cx={x(c.x)} cy={y(c.y)} r={5} ring={1.5}
						onEnter={() => setHover({ cx: x(c.x), lines: [`${cap(cn)} ${c.id}: ${plural(c.n, noun)}`, `Mean ${xl}: ${fmt(c.x)}`, `Mean ${yl}: ${fmt(c.y)}`] })} onLeave={() => setHover(null)} />
				))}
				<text x={box.r + 10} y={endOf("between") - 6} fontSize="12" fill={INK} fontWeight="600">Between {cn}s</text>
				<text x={box.r + 10} y={endOf("between") + 8} fontSize="11" fill={MUTED}>{signed(betweenSlope * unit)} per {unit} of the mean</text>
				{last && <text x={box.r + 10} y={endOf("within") - 6} fontSize="12" fill={INK} fontWeight="600">Within {article(cn)}</text>}
				{last && <text x={box.r + 10} y={endOf("within") + 8} fontSize="11" fill={MUTED}>{signed(withinSlope * unit)} per {unit}</text>}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Within {article(cn)}, {article(noun)} {unit} above their {cn}'s average on {xl} scores {fmt(Math.abs(withinSlope * unit))} {withinSlope < 0 ? "lower" : "higher"} on {yl}.
				Between {cn}s, {article(cn)} whose mean is {unit} higher scores {fmt(Math.abs(betweenSlope * unit))} {betweenSlope < 0 ? "lower" : "higher"} on average.
				The two slopes answer different questions; reading the between slope as what happens to one {noun} is the ecological fallacy.
			</figcaption>
		</figure>
	);
}

// ---- caterpillar: each cluster's random intercept with its 95% interval, sorted ----

function CaterpillarChart({ effects, clusterNoun, sd, axisLabel, noun }) {
	const [hover, setHover] = useState(null);
	const k = effects.length, cn = clusterNoun;
	const height = 300, box = { l: 48, r: W - M.right, t: M.top + 18, b: height - 40 };
	const lows = effects.map((e) => e.lower), highs = effects.map((e) => e.upper), span = Math.max(...highs) - Math.min(...lows);
	// Headroom above the highest interval and below the lowest for their direct labels.
	const axY = axis([...lows, ...highs, 0, Math.min(...lows) - 0.08 * span, Math.max(...highs) + 0.08 * span], 0.02);
	const axX = { min: 0.5, max: k + 0.5, ticks: [] };
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const clear = effects.filter((e) => e.lower > 0 || e.upper < 0).length;
	const half = (x(2) - x(1)) / 2;
	const low = effects[0], high = effects[k - 1];
	const outcome = lowerFirst(axisLabel || "the outcome");
	return (
		<figure>
			<Legend items={[{ label: `One ${cn}'s estimated intercept, with its 95% interval`, color: CHART_2 }, { label: `Interval includes 0`, kind: "ring" },
				{ label: `The average ${cn} (the fixed intercept)`, color: CHART_1, kind: "line" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Estimated intercepts for ${k} ${cn}s, sorted, from ${signed(low.estimate)} (${cn} ${low.id}) to ${signed(high.estimate)} (${cn} ${high.id}) points of ${outcome} relative to the average ${cn}. ${clear} of ${k} intervals exclude zero.`}>
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} yLabel={`${cap(cn)}'s intercept: ${outcome} above (+) or below (−) the average ${cn}`} />
				<text x={box.r} y={box.b + 18} fontSize="11" fill={MUTED} textAnchor="end">{cap(cn)}s, sorted by their intercept</text>
				<line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={CHART_1} strokeWidth="1.5" />
				{effects.map((e, i) => {
					const cx = x(i + 1), sure = e.lower > 0 || e.upper < 0;
					return (
						<g key={e.id} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
							<rect x={cx - half} y={box.t} width={2 * half} height={box.b - box.t} fill="transparent" />
							<line x1={cx} x2={cx} y1={y(e.lower)} y2={y(e.upper)} stroke={CHART_2} strokeWidth={hover === i ? 2.5 : 1.5} />
							<circle cx={cx} cy={y(e.estimate)} r="4" fill={sure ? CHART_2 : PAPER} stroke={sure ? PAPER : CHART_2} strokeWidth={sure ? 1.5 : 2} />
						</g>
					);
				})}
				<Halo x={x(1) - 4} y={y(low.lower) + 15} fill={INK} fontWeight="600">{cap(cn)} {low.id}</Halo>
				<Halo x={x(k) + 4} y={y(high.upper) - 7} fill={INK} fontWeight="600" textAnchor="end">{cap(cn)} {high.id}</Halo>
				{hover !== null && <Tooltip x={x(hover + 1)} lines={[`${cap(cn)} ${effects[hover].id} (${plural(effects[hover].n, noun)})`, `Intercept ${signed(effects[hover].estimate)}`,
					`95% interval ${fmt(effects[hover].lower)} to ${fmt(effects[hover].upper)}`]} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each {cn}'s departure from the average {cn}, after the model's predictors: its conditional mode ± 1.96 conditional SDs (lme4's ranef).
				{sd != null ? ` Across ${cn}s like these the departures have SD ${fmt(sd)}.` : ""} {clear} of {k} intervals exclude zero. The estimates are pulled toward zero, most for the smallest {cn}s, so they spread less than raw {cn} means would.
			</figcaption>
		</figure>
	);
}

// ---- profiles by group: a sample of people per group, coloured by group, with every person's means ----

// groups [{ name, total, ids, series [[score or null]], means [per condition, everyone], present, possible }]
function GroupedProfilesChart({ groups, labels, axisLabel, noun, legendTitle }) {
	const [hover, setHover] = useState(null);
	const colors = [CHART_1, CHART_2, CHART_3];
	const k = labels.length;
	const height = 320, box = { l: 44, r: W - 128, t: M.top + 16, b: height - 40 };
	const all = groups.flatMap((g) => [...g.series.flat(), ...g.means]).filter((v) => v !== null);
	const axY = axis(all);
	const y = scale(axY, box.b, box.t);
	const x = (i) => box.l + 20 + (i / (k - 1)) * (box.r - box.l - 40);
	// Runs of consecutive scores, and the dashed bridges across missed occasions.
	const runs = (s) => {
		const out = [], bridges = [];
		let cur = [], prev = null;
		s.forEach((v, i) => {
			if (v === null) { if (cur.length) out.push(cur); cur = []; return; }
			if (prev !== null && prev !== i - 1) bridges.push([[prev, s[prev]], [i, v]]);
			cur.push([i, v]); prev = i;
		});
		if (cur.length) out.push(cur);
		return { out, bridges };
	};
	const pts = (run) => run.map(([i, v]) => `${x(i)},${y(v)}`).join(" ");
	const shown = groups.reduce((a, g) => a + g.series.length, 0), total = groups.reduce((a, g) => a + g.total, 0);
	const present = groups.reduce((a, g) => a + g.present, 0), possible = groups.reduce((a, g) => a + g.possible, 0);
	const ends = spread(groups.map((g, j) => ({ j, y: y(g.means[k - 1]) + 4 })), 30);
	const score = (v) => (v === null ? "—" : Number.isInteger(v) ? String(v) : fmt(v, 1));
	const named = groups.length > 1;
	// Several groups take chart-1, chart-2, chart-3; one group keeps its people green (data) and its mean teal.
	const lineColor = (j) => (named ? colors[j] : CHART_2), meanColor = (j) => (named ? colors[j] : CHART_1);
	return (
		<figure>
			<Legend title={named ? legendTitle : undefined} items={[
				...(named ? groups.map((g, j) => ({ label: g.name, color: colors[j], kind: "line" })) : [{ label: `One ${noun}`, color: CHART_2, kind: "line" }]),
				named ? { label: "Thick: the mean of everyone's scores", color: MUTED, kind: "line" } : { label: `Mean of all ${total} ${noun}s`, color: CHART_1, kind: "line" },
				...(present < possible ? [{ label: "Dashed: across a missed occasion", color: MUTED, kind: "dash" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`${shown} of ${total} ${noun}s' scores across ${k} occasions${named ? `, ${groups.map((g) => `${g.series.length} of ${g.total} from ${g.name}`).join(" and ")}` : ""}. ${present} of ${possible} possible scores are present. Means${named ? " by group" : ""}, from everyone: ${groups.map((g) => `${named ? `${g.name} ` : ""}${fmt(g.means[0], 1)} to ${fmt(g.means[k - 1], 1)}`).join("; ")}.`}>
				<Axes axX={{ ticks: [] }} axY={axY} x={x} y={y} box={box} yLabel={axisLabel} />
				{labels.map((l, i) => <text key={l} x={x(i)} y={box.b + 18} fontSize="11" fill={INK} textAnchor="middle">{l}</text>)}
				{groups.map((g, j) => g.series.map((s, i) => {
					const { out, bridges } = runs(s), on = hover && hover.j === j && hover.i === i, dim = hover && !on;
					const tip = { j, i, cx: x(k - 1), lines: [`${cap(noun)} ${g.ids[i]}${named ? `, ${g.name}` : ""}`, s.map(score).join(" → "), `${s.filter((v) => v === null).length} of ${k} occasions missed`] };
					return (
						<g key={`${j}-${g.ids[i]}`} opacity={dim ? 0.2 : on ? 1 : 0.55} onMouseEnter={() => setHover(tip)} onMouseLeave={() => setHover(null)}>
							{out.map((run, r) => run.length > 1
								? <polyline key={r} points={pts(run)} fill="none" stroke={lineColor(j)} strokeWidth={on ? 2.5 : 1.25} strokeLinejoin="round" />
								: <circle key={r} cx={x(run[0][0])} cy={y(run[0][1])} r="2.5" fill={lineColor(j)} />)}
							{bridges.map(([a, b], r) => <line key={`b${r}`} x1={x(a[0])} y1={y(a[1])} x2={x(b[0])} y2={y(b[1])} stroke={lineColor(j)} strokeWidth="1" strokeDasharray="3 3" />)}
							<polyline points={s.map((v, n) => (v === null ? null : `${x(n)},${y(v)}`)).filter(Boolean).join(" ")} fill="none" stroke="transparent" strokeWidth="8" />
						</g>
					);
				}))}
				{groups.map((g, j) => (
					<g key={`m${j}`} onMouseEnter={() => setHover({ j, mean: true, cx: x(k - 1), lines: [`${named ? `${g.name}: ` : ""}mean of all ${g.total} ${noun}s`, g.means.map((v) => score(v === null ? null : +v.toFixed(1))).join(" → ")] })}
						onMouseLeave={() => setHover(null)}>
						<polyline points={g.means.map((v, i) => `${x(i)},${y(v)}`).join(" ")} fill="none" stroke="transparent" strokeWidth="10" />
						<polyline points={g.means.map((v, i) => `${x(i)},${y(v)}`).join(" ")} fill="none" stroke={meanColor(j)} strokeWidth="3" strokeLinejoin="round" />
					</g>
				))}
				{groups.map((g, j) => (
					<g key={`e${j}`}>
						<text x={box.r + 8} y={ends[j].y - 6} fontSize="12" fill={INK} fontWeight="600">{named ? g.name : "Mean"}</text>
						<text x={box.r + 8} y={ends[j].y + 8} fontSize="11" fill={MUTED}>{fmt(g.means[0], 1)} → {fmt(g.means[k - 1], 1)}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx - 200} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Thin lines: {named ? `${groups[0].series.length} ${noun}s drawn at random from each ${lowerFirst(legendTitle || "group")} (${groups.map((g) => `${g.series.length} of ${g.total}`).join(", ")})` : `${shown} of ${total} ${noun}s, drawn at random`}. Thick lines: the mean at each occasion of everyone who answered then.
				{present < possible ? ` ${possible - present} of ${possible} scores (${Math.round((100 * (possible - present)) / possible)}%) are missing, so each mean is over the ${noun}s present that week, not a fixed set.` : ""}
			</figcaption>
		</figure>
	);
}

// ---- msv_dots: AVE, MSV and ASV per construct on one 0–1 axis ----

function MsvDotsChart({ constructs }) {
	const [hover, setHover] = useState(null);
	const labelW = 110, rowH = 52, top = M.top + 12;
	const box = { l: M.left + labelW, r: W - 150, t: top, b: top + rowH * constructs.length };
	const height = box.b + 34;
	const ax = { min: 0, max: 1, ticks: [0, 0.2, 0.4, 0.6, 0.8, 1] };
	const x = scale(ax, box.l, box.r);
	const tick = (t) => (t === 0 || t === 1 ? t : fmtR(t, 1));
	const marks = [
		{ key: "ave", name: "AVE", color: CHART_1, prefer: "above" },
		{ key: "msv", name: "MSV", color: CHART_2, prefer: "below" },
		{ key: "asv", name: "ASV", color: null, prefer: "above" }
	];
	// Each value's direct label, above or below its dot, whichever doesn't collide.
	const labelsFor = (c) => {
		const taken = [];
		return marks.map((m) => {
			const cx = x(c[m.key]), w = 44;
			const clash = (side) => taken.some((t) => t.side === side && Math.abs(t.cx - cx) < w);
			const side = !clash(m.prefer) ? m.prefer : m.prefer === "above" ? "below" : "above";
			taken.push({ side, cx });
			return { ...m, cx, side };
		});
	};
	const tip = (c, m) => {
		const v = c[m.key];
		if (m.key === "ave") return [`${c.name}: AVE ${fmtR(v)}`, `Explains ${Math.round(v * 100)}% of its items' variance, on average`];
		if (m.key === "msv") return [`${c.name}: MSV ${fmtR(v)}`, c.partner ? `Shared with ${c.partner}: r = ${fmtR(c.r)}, r² = ${fmtR(v)}` : "Its largest squared correlation"];
		return [`${c.name}: ASV ${fmtR(v)}`, "Its average squared correlation with the other constructs"];
	};
	const fails = constructs.filter((c) => c.msv >= c.ave);
	return (
		<figure>
			<Legend items={[{ label: "AVE: its own items' variance it explains", color: CHART_1 }, { label: "MSV: the most it shares with another construct", color: CHART_2 },
				{ label: "ASV: the average it shares", kind: "ring" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`AVE, MSV and ASV for ${constructs.length} constructs. ${constructs.map((c) => `${c.name}: AVE ${fmtR(c.ave)}, MSV ${fmtR(c.msv)}, ASV ${fmtR(c.asv)}, ${c.msv < c.ave ? "MSV below AVE" : "MSV above AVE"}`).join("; ")}.`}>
				{ax.ticks.map((t) => (
					<g key={t}>
						<line x1={x(t)} x2={x(t)} y1={box.t} y2={box.b} stroke={GRID} />
						<text x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{tick(t)}</text>
					</g>
				))}
				<text x={box.r} y={box.b + 30} fontSize="11" fill={MUTED} textAnchor="end">Share of variance (0 to 1)</text>
				{constructs.map((c, i) => {
					const cy = box.t + rowH * i + rowH / 2, pass = c.msv < c.ave;
					const [a, b] = [x(Math.min(c.msv, c.ave)), x(Math.max(c.msv, c.ave))];
					return (
						<g key={c.name}>
							<text x={M.left} y={cy} fontSize="12" fill={INK} fontWeight="600" dominantBaseline="middle">{c.name}</text>
							<line x1={a} x2={b} y1={cy} y2={cy} stroke={pass ? CHART_1 : MUTED} strokeWidth="6" strokeLinecap="round" opacity={pass ? 0.22 : 0.35}
								strokeDasharray={pass ? undefined : "2 5"} />
							{labelsFor(c).map((m) => (
								<g key={m.key} onMouseEnter={() => setHover({ cx: m.cx, lines: tip(c, m) })} onMouseLeave={() => setHover(null)}>
									{m.color ? <circle cx={m.cx} cy={cy} r="6" fill={m.color} stroke={PAPER} strokeWidth="2" /> : <circle cx={m.cx} cy={cy} r="5" fill={PAPER} stroke={MUTED} strokeWidth="1.5" />}
									<Halo x={m.cx} y={m.side === "above" ? cy - 11 : cy + 19} fill={m.color ? INK : MUTED} textAnchor="middle">{m.name} {fmtR(c[m.key])}</Halo>
								</g>
							))}
							<text x={box.r + 16} y={cy - 6} fontSize="12" fill={pass ? INK : DANGER} fontWeight="600">{pass ? "MSV < AVE: passes" : "MSV ≥ AVE: fails"}</text>
							<text x={box.r + 16} y={cy + 9} fontSize="11" fill={MUTED}>AVE − MSV = {fmtR(c.ave - c.msv)}</text>
						</g>
					);
				})}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Discriminant validity asks each construct to explain more of its own items' variance (AVE) than it shares with any other construct (MSV, its largest squared correlation), so MSV should sit left of AVE.
				{fails.length ? ` ${fails.map((c) => `${c.name} shares ${fmtR(c.msv)} with ${c.partner || "another construct"} but explains only ${fmtR(c.ave)} of its own items`).join("; ")}.` : " Every construct passes."}
			</figcaption>
		</figure>
	);
}

// ---- item_scatter: each item's difficulty p against its corrected item–total r ----

// items [{ key, name, p, r, d?, rescored? { p, r } }], difficultyBand [lo, hi], cutoffs [ascending],
// discriminationLabels (one per band, lowest first).
function ItemScatterChart({ items, difficultyBand, cutoffs = [], discriminationLabels }) {
	const [hover, setHover] = useState(null);
	const id = React.useId().replace(/:/g, "");
	const height = 320, box = { l: 48, r: W - M.right - 104, t: M.top + 18, b: height - 40 };
	const axX = { min: 0, max: 1, ticks: [0, 0.2, 0.4, 0.6, 0.8, 1] };
	const rs = items.flatMap((it) => [it.r, ...(it.rescored ? [it.rescored.r] : [])]);
	const axY = axis([...rs, ...cutoffs, 0, Math.max(...rs) + 0.05], 0.05);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const tick = (t) => (t === 0 || t === 1 ? t : fmtR(t, Math.abs(t * 10 - Math.round(t * 10)) > 1e-9 ? 2 : 1));
	const bands = [axY.min, ...cutoffs, axY.max].slice(0, -1).map((lo, i, arr) => ({ lo, hi: i + 1 < arr.length ? arr[i + 1] : axY.max }));
	const bandName = (i) => (discriminationLabels && discriminationLabels[i]) || (i === 0 ? `Below ${fmtR(cutoffs[0])}` : i === cutoffs.length ? `${fmtR(cutoffs[i - 1])} and up` : `${fmtR(cutoffs[i - 1])} to ${fmtR(cutoffs[i])}`);
	const bandOf = (r) => cutoffs.filter((c) => r >= c).length;
	const inBand = (p) => !difficultyBand || (p >= difficultyBand[0] && p <= difficultyBand[1]);
	// Direct labels: beside each dot, on whichever side collides with nothing already placed.
	const placedLabels = useMemo(() => {
		const dots = items.flatMap((it) => [[x(it.p), y(it.r)], ...(it.rescored ? [[x(it.rescored.p), y(it.rescored.r)]] : [])]);
		const boxes = [];
		// How many placed labels and dots a label box would touch, or a large count outside the plot.
		const clashes = (b) => (b.x1 > box.r || b.x0 < box.l || b.y0 < box.t || b.y1 > box.b ? 99 : 0) +
			boxes.filter((o) => b.x0 < o.x1 + 2 && b.x1 > o.x0 - 2 && b.y0 < o.y1 + 1 && b.y1 > o.y0 - 1).length +
			dots.filter(([dx, dy]) => dx > b.x0 - 6 && dx < b.x1 + 6 && dy > b.y0 - 6 && dy < b.y1 + 6).length;
		const place = (text, cx, cy) => {
			const w = text.length * 6.2, h = 12;
			const options = [[cx + 9, cy + 4, "start"], [cx - 9, cy + 4, "end"], [cx, cy - 10, "middle"], [cx, cy + 18, "middle"],
				[cx + 8, cy - 9, "start"], [cx - 8, cy - 9, "end"], [cx + 8, cy + 17, "start"], [cx - 8, cy + 17, "end"], [cx, cy - 22, "middle"], [cx, cy + 30, "middle"]];
			const scored = options.map(([lx, ly, anchor]) => {
				const x0 = anchor === "start" ? lx : anchor === "end" ? lx - w : lx - w / 2;
				const b = { x0, x1: x0 + w, y0: ly - h + 2, y1: ly + 2 };
				return { lx, ly, anchor, b, n: clashes(b) };
			});
			const best = scored.reduce((a, o) => (o.n < a.n ? o : a));
			boxes.push(best.b);
			return best;
		};
		return items.map((it) => place(it.name, x(it.p), y(it.r)));
	}, [items]);
	const tip = (it, re) => {
		const v = re ? it.rescored : it;
		return [re ? `${it.name}, rescored with the right key` : it.name, `p = ${fmtR(v.p)}: ${Math.round(v.p * 100)}% got it right`, `Corrected item–total r = ${fmtR(v.r)}`,
			...(!re && it.d != null ? [`D = ${fmtR(it.d)}`] : []), `${inBand(v.p) ? "Inside" : "Outside"} the p band; ${bandName(bandOf(v.r)).toLowerCase()}`];
	};
	const outside = items.filter((it) => !inBand(it.p)), weak = cutoffs.length ? items.filter((it) => it.r < cutoffs[cutoffs.length - 1]) : [];
	const list = (its, f) => sentenceList(its.map((it) => `${it.name} (${fmtR(f(it))})`));
	return (
		<figure>
			<Legend items={[{ label: "One item", color: CHART_2 }, ...(difficultyBand ? [{ label: `p from ${fmtR(difficultyBand[0])} to ${fmtR(difficultyBand[1])}`, color: TINT_1, kind: "square" }] : []),
				...(cutoffs.length ? [{ label: `r = ${cutoffs.map((c) => fmtR(c)).join(" and ")}`, color: MUTED, kind: "dash" }] : []),
				...(items.some((it) => it.rescored) ? [{ label: `${sentenceList(items.filter((it) => it.rescored).map((it) => it.name))} rescored with the corrected key`, kind: "ring" }] : [])]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Difficulty against discrimination for ${items.length} items. ${items.map((it) => `${it.name}: p ${fmtR(it.p)}, r ${fmtR(it.r)}`).join("; ")}.`}>
				<defs>
					<marker id={`${id}-arrow`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0,0 L10,5 L0,10 Z" fill={MUTED} /></marker>
				</defs>
				{difficultyBand && <rect x={x(difficultyBand[0])} y={box.t} width={x(difficultyBand[1]) - x(difficultyBand[0])} height={box.b - box.t} fill={CHART_1} opacity="0.07" />}
				<Axes axX={axX} axY={axY} x={x} y={y} box={box} xLabel="Difficulty p: share who got it right" yLabel="Corrected item–total r" xFormat={tick} yFormat={tick} />
				<line x1={box.l} x2={box.r} y1={y(0)} y2={y(0)} stroke={MUTED} strokeWidth="1" />
				{cutoffs.map((c) => <line key={c} x1={box.l} x2={box.r} y1={y(c)} y2={y(c)} stroke={MUTED} strokeWidth="1.25" strokeDasharray="5 4" />)}
				{bands.map((b, i) => {
					const lo = Math.max(b.lo, axY.min), hi = Math.min(b.hi, axY.max);
					return <text key={i} x={box.r + 8} y={(y(lo) + y(hi)) / 2} fontSize="11" fill={MUTED} dominantBaseline="middle">{bandName(i)}</text>;
				})}
				{items.map((it, i) => it.rescored && (
					<g key={`re${it.key}`} onMouseEnter={() => setHover({ cx: x(it.rescored.p), lines: tip(it, true) })} onMouseLeave={() => setHover(null)}>
						<line x1={x(it.p)} y1={y(it.r)} x2={x(it.rescored.p)} y2={y(it.rescored.r)} stroke={MUTED} strokeWidth="1" strokeDasharray="3 3" markerEnd={`url(#${id}-arrow)`} />
						<circle cx={x(it.rescored.p)} cy={y(it.rescored.r)} r="5" fill={PAPER} stroke={CHART_2} strokeWidth="2" />					</g>
				))}
				{items.map((it, i) => (
					<g key={it.key} onMouseEnter={() => setHover({ cx: x(it.p), lines: tip(it, false) })} onMouseLeave={() => setHover(null)}>
						<Dot cx={x(it.p)} cy={y(it.r)} r={5} ring={1.5} />
						<Halo x={placedLabels[i].lx} y={placedLabels[i].ly} textAnchor={placedLabels[i].anchor} fill={INK}>{it.name}</Halo>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				{difficultyBand ? `Outside the shaded p band: ${outside.length ? list(outside, (it) => it.p) : "none"}. ` : ""}
				{cutoffs.length ? `Below r = ${fmtR(cutoffs[cutoffs.length - 1])}: ${weak.length ? list(weak, (it) => it.r) : "none"}. ` : ""}
				An item near p = .50 has the most room to separate students; a negative r means students who got it right did worse on the rest.
			</figcaption>
		</figure>
	);
}

function Chart({ kind, values = [], groups = {}, pairs = [], series = [], points = [], categories = [], unit = "",
	df = 30, df1 = 2, df2 = 30, dfNote, observed = 1, symbol, axisLabel = "", noun = "participant", xLabel = "x", yLabel = "y", labels = ["Before", "After"], legendTitle, ...rest }) {
	const fDensity = useMemo(() => (v) => fPdf(v, df1, df2), [df1, df2]);
	const chiDensity = useMemo(() => (v) => chi2Pdf(v, df), [df]);
	const words = { axisLabel, noun };
	switch (kind) {
		case "sd": return <SdChart values={values} axisLabel={axisLabel} noun={noun} />;
		case "se": return <SeChart values={values} axisLabel={axisLabel} noun={noun} />;
		case "df": return <DfChart df={df} />;
		case "groups": return <GroupsChart groups={groups} axisLabel={axisLabel} noun={noun} />;
		case "ranks": return <GroupsChart groups={groups} axisLabel={axisLabel || "Rank (1 = lowest)"} noun={noun} meanLabel="Mean rank" grandLabel="Middle rank" />;
		case "scatter": return <ScatterChart points={points} xLabel={xLabel} yLabel={yLabel} noun={noun} />;
		case "paired": return <ProfilesChart series={pairs} labels={labels} axisLabel={axisLabel} noun={noun} />;
		case "profiles": return Array.isArray(groups)
			? <GroupedProfilesChart groups={groups} labels={labels} axisLabel={axisLabel} noun={noun} legendTitle={legendTitle} />
			: <ProfilesChart series={series} labels={labels} axisLabel={axisLabel} noun={noun} />;
		// Without degrees of freedom (settings supply them for factorial and covariate designs) there's no curve to draw.
		case "f": return df1 == null || df2 == null ? null
			: <DistChart pdf={fDensity} name="F" symbol={symbol || "F"} dfText={`df = ${dfLabel(df1)} and ${dfLabel(df2)}${dfNote ? ` (${dfNote})` : ""}`} observed={observed} />;
		case "chi2": return df == null ? null : <DistChart pdf={chiDensity} name="χ²" symbol={symbol || "χ²"} dfText={`df = ${dfLabel(df)}`} observed={observed} />;
		case "bars": return <BarsChart categories={categories} series={series} unit={unit} legendTitle={legendTitle} />;
		case "histogram": return <HistogramChart values={values} {...words} {...rest} />;
		case "qq": return <QqChart {...words} {...rest} />;
		case "identity": return <IdentityChart pairs={pairs} labels={labels} noun={noun} />;
		case "bland_altman": return <BlandAltmanChart points={points} labels={labels} noun={noun} {...rest} />;
		case "loadings": return <LoadingsChart groups={Array.isArray(groups) ? groups : undefined} {...rest} />;
		case "heatmap": return <HeatmapChart {...rest} />;
		case "rank_scatter": return <RankScatterChart points={points} xLabel={xLabel} yLabel={yLabel} noun={noun} {...rest} />;
		case "samples_scatter": return <SamplesScatterChart xLabel={xLabel} yLabel={yLabel} noun={noun} {...rest} />;
		case "interaction": return <InteractionChart axisLabel={axisLabel} {...rest} />;
		case "path": return <PathChart {...rest} />;
		case "residuals": return <ResidualsChart points={points} noun={noun} {...rest} />;
		case "group_lines": return <GroupLinesChart groups={groups} xLabel={xLabel} {...words} {...rest} />;
		case "pair_counting": return <PairCountingChart noun={noun} {...rest} />;
		case "icc": return <IccChart xLabel={xLabel} yLabel={yLabel} {...rest} />;
		case "information": return <InformationChart xLabel={xLabel} {...rest} />;
		case "scree": return <ScreeChart {...rest} />;
		case "dif_bars": return <BarsChart categories={categories} series={series} unit={unit} legendTitle={legendTitle} />;
		case "agreement": return <AgreementChart noun={noun} {...rest} />;
		case "roc": return <RocChart points={points} noun={noun} {...rest} />;
		case "matrix": return <MatrixChart {...rest} />;
		case "path_diagram": return <PathDiagramChart {...rest} />;
		case "within_between": return <WithinBetweenChart xLabel={xLabel} yLabel={yLabel} {...words} {...rest} />;
		case "caterpillar": return <CaterpillarChart {...words} {...rest} />;
		case "msv_dots": return <MsvDotsChart {...rest} />;
		case "item_scatter": return <ItemScatterChart {...rest} />;
		case "clusters": return <ClustersChart noun={noun} legendTitle={legendTitle} {...rest} />;
		case "cluster_profiles": return <ClusterProfilesChart noun={noun} legendTitle={legendTitle} {...rest} />;
		case "dendrogram": return <DendrogramChart noun={noun} legendTitle={legendTitle} {...rest} />;
		case "silhouette_by_k": return <SilhouetteByKChart noun={noun} {...rest} />;
		case "silhouette_plot": return <SilhouettePlotChart noun={noun} legendTitle={legendTitle} {...rest} />;
		case "group_ellipses": return <GroupEllipsesChart groups={groups} xLabel={xLabel} yLabel={yLabel} legendTitle={legendTitle} noun={noun} {...rest} />;
		case "discriminant_axes": return <DiscriminantAxesChart groups={groups} legendTitle={legendTitle} noun={noun} {...rest} />;
		case "r2_blocks": return <R2BlocksChart {...rest} />;
		case "added_variable": return <AddedVariableChart points={points} noun={noun} {...rest} />;
		case "simple_slopes": return <SimpleSlopesChart points={points} noun={noun} {...rest} />;
		case "johnson_neyman": return <JohnsonNeymanChart noun={noun} {...rest} />;
		case "partial_scatter": return <PartialScatterChart noun={noun} {...rest} />;
		case "binomial": return <BinomialChart {...rest} />;
		case "binomial_power": return <BinomialChart {...rest} />;
		case "prior_posterior": return <PriorPosteriorChart {...rest} />;
		case "bf_robustness": return <BfRobustnessChart points={points} {...rest} />;
		case "coincidence": return <CoincidenceChart labels={labels} {...rest} />;
		case "dot_plot": return <DotPlotChart groups={groups} axisLabel={axisLabel} noun={noun} {...rest} />;
		default: return null;
	}
}

// .chart scopes the dark-mode tokens (app/assets/stylesheets/charts.css) to the legend and caption HTML.
export default function ConceptChart(props) {
	return <div className="chart"><Chart {...props} /></div>;
}
